/* cache-bump 2026-08-03b: Shopify's CDN held stale bytes under the then-current asset
   version key (AMOM200 still flagged pending) even though the theme file was correct.
   Re-pushing identical bytes does not restamp the version, so this line exists purely
   to change the checksum and force a fresh asset URL. Safe to delete on any later edit. */
/* bundles-app.jsx — 100X Equine bundle builder.
   DATA-DRIVEN: swap PRODUCTS prices + the VOLUME curve (volPct / VOL_STATUS) from
   the spreadsheet and the whole page reprices. Discount is keyed to TOTAL UNITS
   (gallons), so it keeps building for big-volume barn orders. Gut X numbers are
   REAL (MSRP 134.84). All other prices + the volume curve are PLACEHOLDER.    */
(function () {
  const {
    useState
  } = React;
  const money = n => '$' + n.toFixed(2);

  /* count-up/down tween so the price visibly moves on every change */
  function useAnimatedNumber(value, dur = 480) {
    const [disp, setDisp] = React.useState(value);
    const fromRef = React.useRef(value);
    React.useEffect(() => {
      const from = fromRef.current,
        to = value,
        start = performance.now();
      if (from === to) return;
      let raf;
      const tick = now => {
        const p = Math.min(1, (now - start) / dur);
        const e = 1 - Math.pow(1 - p, 3);
        setDisp(from + (to - from) * e);
        if (p < 1) raf = requestAnimationFrame(tick);else fromRef.current = to;
      };
      raf = requestAnimationFrame(tick);
      // safety net: if rAF is throttled (background tab), still land the final value
      const settle = setTimeout(() => {
        setDisp(to);
        fromRef.current = to;
      }, dur + 80);
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(settle);
        fromRef.current = to;
      };
    }, [value]);
    return disp;
  }
  function usePrevious(v) {
    const ref = React.useRef();
    React.useEffect(() => {
      ref.current = v;
    }, [v]);
    return ref.current;
  }

  /* ---- Catalog. Prices are STATIC from the pricing sheet (1-unit = sub; 2/4-unit
     totals live in `tiers`). Logic layer (bundle matching etc.) handled separately. ---- */
  const PRODUCTS = [{
    key: 'gut-x',
    name: 'Gut X',
    cat: 'Gut & Immune',
    unit: '1 gallon · 128 day supply',
    desc: 'Liquid gut and immune support. The foundation every other protocol builds on.',
    bestFor: 'All horses',
    daysPerUnit: 128,
    results: '5–7 days',
    actives: ['Beta glucan', 'Hyaluronan', 'Liquid Delivery'],
    benefits: [{
      icon: 'gut',
      text: 'Gastric Support',
      sub: 'Coats and soothes the stomach lining.'
    }, {
      icon: 'coat',
      text: 'Body Condition',
      sub: 'Better absorption for weight and topline.'
    }, {
      icon: 'shield',
      text: 'Colic Support',
      sub: 'Up to $20,000 in colic surgery coverage.'
    }],
    pairsLabel: 'Foundational · Pairs well with',
    supply: '3 to 4 month supply',
    supplyShort: '3 to 4 mo. supply',
    supplyMobile: true,
    bestForNote: 'all breeds & ages',
    resultsNote: 'through 180 days',
    bestForTip: 'Formulated for every horse: performance, seniors, broodmares, and easy keepers. One daily maintenance dose, adjusted by body weight.',
    resultsTip: 'Most owners notice manure quality, appetite, and coat changes in the first week. Gut lining support keeps compounding, with full benefit typically by month 6.',
    supplyTip: 'One gallon is 128 maintenance doses. At the standard daily pump, that covers one horse for 3 to 4 months. The initial loading phase runs about 30 days.',
    pairs: ['gut-x', 'osteo-max', 'max-calm', 'joint-flex-plus', 'amino-max', 'hydra-max'],
    msrp: 134.79,
    tiers: [{
      q: 2,
      price: 212.56
    }, {
      q: 4,
      price: 402.56
    }],
    icon: 'shield',
    accent: '#ED5757',
    sku: 'GX',
    img: window.BA["cfJ8qBBg.webp"],
    real: true
  }, {
    key: 'osteo-max',
    name: 'Osteo-MAX',
    cat: 'Joint, Soft Tissue & Bone',
    unit: '1 gallon · ~30 day supply',
    desc: 'Bone density, joint integrity, and recovery for hard-working and aging horses.',
    bestFor: 'All horses',
    daysPerUnit: 128,
    results: '14–21 days',
    actives: ['Vit K2 (MK7)', 'Hyaluronan', 'Orthosilicic acid'],
    benefits: [{
      icon: 'joint',
      text: 'Joint comfort & mobility',
      sub: 'Eases stiffness and keeps joints moving.'
    }, {
      icon: 'bone',
      text: 'Bone density',
      sub: 'Supports strong bone under work and age.'
    }, {
      icon: 'muscle',
      text: 'Soft-tissue recovery',
      sub: 'Helps tendons and ligaments bounce back.'
    }],
    pairsLabel: 'Joint, Soft Tissue & Bone · Pairs well with',
    supply: '3 to 4 month supply',
    supplyShort: '3 to 4 mo. supply',
    supplyMobile: true,
    bestForNote: 'all competition levels',
    resultsNote: 'through 180 days',
    bestForTip: 'Built for horses in hard work and for seniors: supports joints, soft tissue, and bone density under load. One daily dose, adjusted by body weight.',
    resultsTip: 'Expect easier movement in 2–3 weeks. Bone and soft tissue remodel slowly, so gains keep building through month 6.',
    supplyTip: 'One gallon is 128 maintenance doses. At the standard daily pump, that covers one horse for 3 to 4 months.',
    pairs: ['gut-x', 'osteo-max', 'joint-flex-plus', 'amino-max', 'hydra-max', 'max-calm'],
    msrp: 231.56,
    tiers: [{
      q: 2,
      price: 414.44
    }, {
      q: 4,
      price: 700.63
    }],
    icon: 'bone',
    accent: '#5B6B8C',
    sku: 'OM',
    img: window.BA["4DmYmZBg.webp"],
    photo: 'Osteo-MAX jug'
  }, {
    key: 'joint-flex-plus',
    name: 'Joint Flex Plus',
    cat: 'Mobility',
    unit: '1 gallon · ~30 day supply',
    desc: 'Everyday joint flexibility and comfort for all horses.',
    bestFor: 'Mobility',
    daysPerUnit: 128,
    actives: ['Hyaluronan', 'Orthosilicic acid', 'Liquid Delivery'],
    benefits: [{
      icon: 'joint',
      text: 'Daily joint flexibility',
      sub: 'Keeps everyday movement smooth and easy.'
    }, {
      icon: 'activity',
      text: 'Comfort in work',
      sub: 'Supports horses in training and competition.'
    }, {
      icon: 'shield',
      text: 'Senior support',
      sub: 'Extra cushioning for aging joints.'
    }],
    pairsLabel: 'Mobility · Pairs well with',
    supply: '3 to 4 month supply',
    supplyShort: '3 to 4 mo. supply',
    supplyMobile: true,
    bestForNote: 'every horse',
    resultsNote: 'through 180 days',
    bestForTip: 'Everyday mobility support for horses in regular work. A lighter maintenance formula than Osteo-MAX, for keeping sound horses moving well.',
    resultsTip: 'Smoother, freer movement typically shows inside two weeks and keeps improving through month 6 of daily feeding.',
    supplyTip: 'One gallon is 128 maintenance doses. At the standard daily pump, that covers one horse for 3 to 4 months.',
    pairs: ['gut-x', 'hydra-max', 'osteo-max', 'joint-flex-plus', 'amino-max', 'max-calm'],
    msrp: 134.79,
    tiers: [{
      q: 2,
      price: 212.56
    }, {
      q: 4,
      price: 402.56
    }],
    icon: 'activity',
    accent: '#3F9E94',
    sku: 'JF',
    img: window.BA["joint-flex-plus.jpg"]
  }, {
    key: 'amino-max',
    name: 'Amino-MAX',
    cat: 'Topline & Muscle',
    unit: '1 gallon · ~30 day supply',
    desc: 'Essential amino acids for topline, muscle development, and recovery.',
    bestFor: 'Topline',
    daysPerUnit: 128,
    results: '14–21 days',
    actives: ['15,250 mg of Amino Acids', 'Biosynthetic Pathway'],
    benefits: [{
      icon: 'muscle',
      text: 'Topline development',
      sub: 'Builds and holds a stronger topline.'
    }, {
      icon: 'trend',
      text: 'Muscle recovery',
      sub: 'Speeds recovery after work and hauling.'
    }, {
      icon: 'nutrition',
      text: 'Maintains muscle at any age',
      sub: 'Supplemented amino acids help horses hold muscle, regardless of age.'
    }],
    pairsLabel: 'Topline & Muscle · Pairs well with',
    supply: '3 to 4 month supply',
    supplyShort: '3 to 4 mo. supply',
    supplyMobile: true,
    bestForNote: 'hard keepers & seniors',
    resultsNote: 'through 180 days',
    bestForTip: 'For horses that need muscle: topline rebuilding, hard keepers, seniors, and horses coming back into work.',
    resultsTip: '15,250 mg of amino acids daily. Topline change is visible on most horses within 14–21 days of consistent feeding, and keeps building through 180 days.',
    supplyTip: 'One gallon is 128 maintenance doses. At the standard daily pump, that covers one horse for 3 to 4 months.',
    pairs: ['gut-x', 'osteo-max', 'joint-flex-plus', 'amino-max', 'hydra-max', 'max-calm'],
    // 2026-08-03 Doug: Amino 2-gallon base cut $279 -> $253 (15% off two singles, up from 6%).
    // WHY. The half-tier rule prices 3 units as 2-tier + half of it. At the old numbers that gave
    // $496.97 against a 4-gallon of $473.81, so 3 gallons cost MORE than 4. Harmless while 3
    // gallons was unbuyable; it becomes a visible defect the moment AM300 exists.
    // Fixed from the 2-gallon side, NOT by raising the 4-gallon to $449 as the launch sheet
    // originally planned, so the 4-gallon subscriber dollar stays held at $379.05 and nobody
    // needs a 90-day price-increase notice. 15% rather than Gut X's 21% because Amino has the
    // highest COGS in the line.
    // Launch ladder is now 176.94 / 300.44 / 450.66 / 473.81 - rising throughout, no inversion.
    // NOTE: pdp-bundle-module.js carries its own copy of this ladder. Change both together.
    msrp: 176.94,
    tiers: [{
      q: 2,
      price: 300.44
    }, {
      q: 4,
      price: 473.81
    }],
    icon: 'trend',
    accent: '#C98A2E',
    sku: 'AM',
    img: window.BA["aminomax-gal-horse.webp"],
    photo: 'Amino-MAX jug'
  }, {
    key: 'hydra-max',
    name: 'Hydra-MAX',
    cat: 'Electrolytes + Creatine',
    unit: 'Bucket · 32 servings',
    desc: 'Daily electrolyte and hydration support for sweat, travel, and heat.',
    bestFor: 'Hydration',
    daysPerUnit: 30,
    results: '30 minutes',
    actives: ['Electrolytes', 'Creatine', 'Glycerol'],
    benefits: [{
      icon: 'drop',
      text: 'Hydration & electrolytes',
      sub: 'Replaces what sweat strips away.'
    }, {
      icon: 'gut',
      text: 'Encourages drinking',
      sub: 'Keeps horses drinking on the road and in heat.'
    }, {
      icon: 'trend',
      text: 'Recovery after work',
      sub: 'Bounce back faster from hot, hard days.'
    }],
    pairsLabel: 'Electrolytes + Creatine · Pairs well with',
    supply: '32 servings',
    supplyShort: '32 servings',
    supplyMobile: true,
    bestForNote: '365 days a year',
    resultsNote: 'for better hydration',
    bestForTip: 'For hauling, showing, and hot weather: replaces the electrolytes sweat strips away and keeps horses drinking on the road.',
    resultsTip: 'Fast-acting: encourages drinking within about 30 minutes of feeding. Use it on sweat days, travel days, and heat waves.',
    supplyTip: 'One bucket holds 32 servings: about a month of daily use, longer if you dose only on work and travel days.',
    pairs: ['gut-x', 'osteo-max', 'joint-flex-plus', 'amino-max', 'hydra-max', 'max-calm'],
    msrp: 117.56,
    tiers: [{
      q: 2,
      price: 224.44
    }],
    icon: 'drop',
    accent: '#3E92C7',
    sku: 'HM',
    img: window.BA["hydramax-hero-barn.webp"],
    photo: 'Hydra-MAX bucket'
  }, {
    key: 'max-calm',
    name: 'Max Calm',
    cat: 'Calm & Focus',
    unit: '1 gallon · ~30 day supply',
    desc: 'Liquid calming support for focus and rideability, without the dullness.',
    bestFor: 'Calming',
    daysPerUnit: 128,
    results: '1 hour',
    actives: ['Ashwagandha', 'Magnesium', 'L-Tryptophan'],
    benefits: [{
      icon: 'moon',
      text: 'Focus & rideability',
      sub: 'Steadier attention under saddle.'
    }, {
      icon: 'shield',
      text: 'No dullness or drag',
      sub: 'Calm without the sluggish, doped feel.'
    }, {
      icon: 'coat',
      text: 'Travel & event stress',
      sub: 'Keeps show and sale horses settled.'
    }],
    pairsLabel: 'Calm & Focus · Pairs well with',
    supply: '3 to 4 month supply',
    supplyShort: '3 to 4 mo. supply',
    supplyMobile: true,
    bestForNote: 'no dull, doped feel',
    resultsNote: 'optimum for 4–6 hrs',
    bestForTip: 'Calm focus without sedation: show horses, sale prep, and hot or anxious horses that need to stay sharp under saddle.',
    resultsTip: 'Feed 4–6 hours before you need the calm: that is the peak window. Daily feeding builds a steadier baseline inside two weeks.',
    supplyTip: 'One gallon is 128 maintenance doses. At the standard daily pump, that covers one horse for 3 to 4 months.',
    pairs: ['gut-x', 'osteo-max', 'joint-flex-plus', 'amino-max', 'hydra-max', 'max-calm'],
    msrp: 134.79,
    tiers: [{
      q: 2,
      price: 212.56
    }, {
      q: 4,
      price: 402.56
    }],
    icon: 'moon',
    accent: '#7E9460',
    sku: 'MC',
    img: window.BA["100X_Content-376.jpg"]
  }];
  const BY_KEY = Object.fromEntries(PRODUCTS.map(p => [p.key, p]));

  /* ---- STATIC bundle pricing from the sheet (set combos at set quantities). ----
     Stored as data for the logic layer (matching / which combo applies) to consume. */
  const BUNDLE_PRICING = [{
    sku: 'OMGX200',
    items: {
      'osteo-max': 1,
      'gut-x': 1
    },
    price: 338.44
  }, {
    sku: 'OMGX400',
    items: {
      'osteo-max': 2,
      'gut-x': 2
    },
    price: 587.81
  }, {
    sku: 'OMGX800',
    items: {
      'osteo-max': 4,
      'gut-x': 4
    },
    price: 1128.13
  }, {
    sku: 'JFGX200',
    items: {
      'joint-flex-plus': 1,
      'gut-x': 1
    },
    price: 212.56
  }, {
    sku: 'JFGX400',
    items: {
      'joint-flex-plus': 2,
      'gut-x': 2
    },
    price: 402.56
  }, {
    sku: 'AMMC200',
    items: {
      'amino-max': 1,
      'max-calm': 1
    },
    price: 295.69
  }, {
    sku: 'AMMC400',
    items: {
      'amino-max': 2,
      'max-calm': 2
    },
    price: 473.81
  }, {
    sku: 'AMGX200',
    items: {
      'amino-max': 1,
      'gut-x': 1
    },
    price: 295.69
  }, {
    sku: 'MCGX200',
    items: {
      'max-calm': 1,
      'gut-x': 1
    },
    price: 212.56
  }, {
    sku: 'OMMC200',
    items: {
      'osteo-max': 1,
      'max-calm': 1
    },
    price: 338.44
  }, {
    sku: 'OMJF200',
    items: {
      'osteo-max': 1,
      'joint-flex-plus': 1
    },
    // ADDED 2026-08-19. Doug caught JF+OM pricing as singles ($366.35 / sub $293.08): this pair
    // was the one OM combo missing from the sheet, and no combo product existed. Product
    // osteo-max-joint-flex-plus (variant OMJF200) created same day at the family price, Recharge
    // plans at 20% on all five cadences. Verified live: one-time $338.44, subscribe $270.75.
    price: 338.44
  }, {
    sku: 'JFMC200',
    items: {
      'joint-flex-plus': 1,
      'max-calm': 1
    },
    // ADDED 2026-08-19 with AMJF200: the two remaining Joint Flex pair omissions (priced as
    // singles, +$57.02 / +$16.04 over the family pattern). Products created at the pattern
    // prices Doug confirmed; Recharge 20% on all five cadences verified before these rows
    // shipped. JF+MC one-time $212.56 / sub $170.05; AM+JF one-time $295.69 / sub $236.55.
    price: 212.56
  }, {
    sku: 'AMJF200',
    items: {
      'amino-max': 1,
      'joint-flex-plus': 1
    },
    price: 295.69
  }, {
    sku: 'AMOM200',
    items: {
      'amino-max': 1,
      'osteo-max': 1
    },
    // ACTIVATED 2026-08-03. Was pending:true because the product was unpublished to the Online
    // Store, so its variant could not be added to a cart and bestCover had to route Amino+Osteo
    // through singles (costing the customer $17.81). Doug published it to all channels and set
    // its Recharge plans to 20%. Verified live: one-time $390.69, subscribe $312.55.
    price: 390.69
  }, {
    sku: 'AMGXOM',
    items: {
      'amino-max': 1,
      'gut-x': 1,
      'osteo-max': 1
    },
    price: 497.56
  }, {
    // ADDED 2026-09-21. Madison reported Gut X + Max Calm at 2 of each quoting $340.10 while the
    // identical four gallons quoted $322.05 elsewhere. CAUSE: only OMGX, JFGX and AMMC carried a
    // 2-of-each row, so bestPrice() could do no better on every other pair than applying its 1+1
    // row twice. That equals plain 2-gallon pricing and loses the 4-unit break (2 x 212.56 =
    // 425.12 against 402.56). The engine was right; the table was short. Seven rows close it.
    // PRICES mirror the structurally identical existing combo, they are not new numbers:
    // family pairs -> JFGX400 402.56, Osteo pairs -> OMGX400 587.81, Amino pairs -> AMMC400
    // 473.81. AMOM400 had no analogue anywhere and is Osteo-MAX's own 4-gallon price (700.63),
    // approved by Doug 2026-09-21. Each row has a real variant created the same day, all seven
    // inheriting their product-level Recharge group (5 cadences, verified 20% on every plan).
    sku: 'MCGX400',
    items: {
      'max-calm': 2,
      'gut-x': 2
    },
    price: 402.56
  }, {
    sku: 'JFMC400',
    items: {
      'joint-flex-plus': 2,
      'max-calm': 2
    },
    price: 402.56
  }, {
    sku: 'OMMC400',
    items: {
      'osteo-max': 2,
      'max-calm': 2
    },
    price: 587.81
  }, {
    sku: 'OMJF400',
    items: {
      'osteo-max': 2,
      'joint-flex-plus': 2
    },
    price: 587.81
  }, {
    sku: 'AMGX400',
    items: {
      'amino-max': 2,
      'gut-x': 2
    },
    price: 473.81
  }, {
    sku: 'AMJF400',
    items: {
      'amino-max': 2,
      'joint-flex-plus': 2
    },
    price: 473.81
  }, {
    sku: 'AMOM400',
    items: {
      'amino-max': 2,
      'osteo-max': 2
    },
    price: 700.63
  }];

  /* ---- VOLUME / "barn" pricing (PLACEHOLDER curve — replace from spreadsheet). ----
     Discount is keyed to TOTAL UNITS (gallons) in the cart, not distinct protocols.
     Every unit past the first lowers the effective per-gallon price, asymptoting
     toward a wholesale floor — so it keeps building for big-volume barn orders. */
  const VOL_FLOOR = 0.42; // (display only) rough max savings for the empty-state estimate
  const BARN_SPEND = 1200; // net revenue to the company where Barn (top tier) is reached
  // named status milestones, gated by $ spent (placeholder thresholds + names)
  const VOL_STATUS = [{
    spend: 0,
    name: 'Member',
    short: 'Member',
    pct: 20
  }, {
    spend: 300,
    name: 'Buckle',
    short: 'Buckle',
    pct: 24
  }, {
    spend: 600,
    name: 'Boots',
    short: 'Boots',
    pct: 28
  }, {
    spend: 900,
    name: 'Cowboy Hat',
    short: 'Cowboy Hat',
    pct: 31
  }, {
    spend: BARN_SPEND,
    name: 'Barn',
    short: 'Barn',
    pct: 35
  }];

  // what the running savings "buys" — placeholder reward values (real $ TBD)
  const REWARDS = [{
    at: 60,
    item: 'a belt buckle'
  }, {
    at: 120,
    item: 'a cowboy hat'
  }, {
    at: 180,
    item: 'a pair of boots'
  }, {
    at: 300,
    item: 'a month of board'
  }];

  /* ---- Curated bundles (proposed). Each is a preset of product keys. ---- */
  const CURATED = [{
    id: 'gut-joint',
    name: 'The Foundation Stack',
    forwho: 'Performance & aging horses',
    desc: 'Our most requested pair. Gut health plus full joint, bone, and soft-tissue support.',
    items: ['gut-x', 'osteo-max'],
    feature: true,
    tag: 'Most popular'
  }, {
    id: 'foundation',
    name: 'The Condition Stack',
    forwho: 'Topline & condition',
    desc: 'Build from the gut up: liquid gut support plus amino acids for topline and muscle.',
    items: ['gut-x', 'amino-max']
  }, {
    id: 'senior',
    name: 'The Senior Stack',
    forwho: 'Older horses in work',
    desc: 'Gut, everyday mobility, and hydration. Three protocols for the hardest-keeping seniors.',
    items: ['gut-x', 'joint-flex-plus', 'hydra-max'],
    tag: 'Best value'
  }, {
    id: 'calm',
    name: 'The Calm Competitor',
    forwho: 'Show & sale horses',
    desc: 'Steady focus on the road plus the gut support that keeps stress from showing.',
    items: ['gut-x', 'max-calm']
  }];
  const round2 = n => Math.round(n * 100) / 100;

  /* ---- Canonical pricing engine (per build spec). Data-driven. ----
     1) productPrice: per-product 1/2/4 tier prices with "half-tier" fill for
        in-between / over quantities (carry the unlocked per-unit rate forward).
     2) bestPrice: cheapest valid cover of the whole cart using defined bundles
        (each consumes its component units) + per-product tier pricing. Memoized.
     3) Subscribe & Save = SUB_DISCOUNT off the best one-time price. */
  function productPrice(p, q) {
    if (q <= 0) return 0;
    const tmap = {
      1: p.msrp
    };
    (p.tiers || []).forEach(t => {
      tmap[t.q] = t.price;
    });
    const t1 = tmap[1],
      t2 = tmap[2],
      t4 = tmap[4];
    if (q === 1) return t1;
    if (t2 == null) return t1 * q;
    if (q === 2) return t2;
    if (t4 == null) return t2 + (q - 2) * (t2 / 2); // no 4-tier: carry 2-tier per-unit
    if (q === 3) return t2 + t2 / 2; // carry 2-tier per-unit
    if (q === 4) return t4;
    return t4 + (q - 4) * (t4 / 4); // carry 4-tier per-unit
  }

  // only published bundles are valid pricing paths (AMOM200 is pending republish)
  const ACTIVE_BUNDLES = BUNDLE_PRICING.filter(b => !b.pending);
  function bestPrice(cart, memo) {
    memo = memo || {};
    const entries = Object.entries(cart).filter(e => e[1] > 0).sort();
    if (entries.length === 0) return 0;
    const key = JSON.stringify(entries);
    if (key in memo) return memo[key];
    let best = entries.reduce((s, e) => s + productPrice(BY_KEY[e[0]], e[1]), 0); // no-bundle baseline
    for (const b of ACTIVE_BUNDLES) {
      if (Object.entries(b.items).every(it => (cart[it[0]] || 0) >= it[1])) {
        const next = {
          ...cart
        };
        for (const it of Object.entries(b.items)) next[it[0]] -= it[1];
        const cost = b.price + bestPrice(next, memo);
        if (cost < best) best = cost;
      }
    }
    memo[key] = best;
    return best;
  }

  // Subscribe & Save: ADDITIONAL % off the best one-time price.
  const SUB_DISCOUNT = 0.20;

  // Bridge to gx-bundle-checkout.js, which turns a cart into real Shopify line items. It needs
  // the SAME ladder, bundle table and half-tier rule to decompose the cart the way this engine
  // priced it. Exposed rather than duplicated: a second copy of the pricing data is exactly how
  // the PDP and bundle ladders drifted apart before.
  try {
    window.GX_BUNDLE_DATA = {
      byKey: BY_KEY,
      bundles: ACTIVE_BUNDLES,
      sub: SUB_DISCOUNT,
      productPrice: productPrice
    };
  } catch (e) {}

  /* full quote for a cart {key: qty} at a given mode. */
  function priceCart(cart, mode) {
    const keys = Object.keys(cart).filter(k => cart[k] > 0);
    const distinct = keys.length;
    let retail = 0,
      units = 0;
    keys.forEach(k => {
      retail += BY_KEY[k].msrp * cart[k];
      units += cart[k];
    });
    retail = round2(retail); // full singles total (strikethrough)
    const oneVol = round2(bestPrice(cart)); // best valid one-time price
    const subVol = round2(oneVol * (1 - SUB_DISCOUNT));
    const base = mode === 'sub' ? subVol : oneVol;
    const total = base;
    const volSavePart = round2(retail - oneVol); // volume / bundle break
    const subSavePart = mode === 'sub' ? round2(oneVol - subVol) : 0; // extra subscribe discount
    const save = round2(retail - total);
    const pct = retail > 0 ? save / retail : 0;
    const perUnit = units > 0 ? total / units : 0;
    // Prorate the REAL (tier/bundle-adjusted) total across line items so the
    // displayed lines always sum to `total`. Weight by each product's standalone
    // one-time tier price, then distribute leftover cents (largest-remainder).
    const lines = {};
    if (keys.length) {
      const weights = keys.map(k => productPrice(BY_KEY[k], cart[k]));
      const wsum = weights.reduce((a, b) => a + b, 0);
      const raw = keys.map((k, i) => wsum > 0 ? base * weights[i] / wsum : base / keys.length);
      const floored = raw.map(v => Math.floor(v * 100) / 100);
      let leftover = Math.round((base - floored.reduce((a, b) => a + b, 0)) * 100);
      const order = raw.map((v, i) => [i, v * 100 - Math.floor(v * 100)]).sort((a, b) => b[1] - a[1]);
      for (let n = 0; n < leftover; n++) floored[order[n % order.length][0]] += 0.01;
      keys.forEach((k, i) => {
        lines[k] = round2(floored[i]);
      });
    }
    return {
      keys,
      distinct,
      units,
      base,
      retail,
      oneVol,
      subVol,
      total,
      save,
      pct,
      perUnit,
      subSavePart,
      volSavePart,
      lines
    };
  }

  /* category glyphs for the small product thumbnails (real photos live in the detail panel) */
  const PROD_ICONS = {
    shield: '<path d="M12 3l7 3v5c0 4.6-3 7.8-7 9-4-1.2-7-4.4-7-9V6l7-3z"/><path d="M9 12l2 2 4-4"/>',
    bone: '<path d="M14.8 4.6a2.1 2.1 0 0 1 3 3 2.1 2.1 0 0 1-.5 3.3l-6.4 6.4A2.1 2.1 0 0 1 7 20.4a2.1 2.1 0 0 1-3-3 2.1 2.1 0 0 1 .5-3.3l6.4-6.4A2.1 2.1 0 0 1 14.8 4.6z"/>',
    activity: '<polyline points="3 12 7 12 10 5 14 19 17 12 21 12"/>',
    trend: '<polyline points="4 17 9 11 13 14 20 6"/><polyline points="15 6 20 6 20 11"/>',
    drop: '<path d="M12 3.5s6 5.6 6 10.1a6 6 0 0 1-12 0c0-4.5 6-10.1 6-10.1z"/>',
    moon: '<path d="M20.5 13.2A8 8 0 1 1 11 3.4a6.2 6.2 0 0 0 9.5 9.8z"/>'
  };
  function ProductMedia({
    p,
    cls
  }) {
    return /*#__PURE__*/React.createElement("div", {
      className: cls
    }, p.img ? /*#__PURE__*/React.createElement("img", {
      src: p.img,
      alt: p.name
    }) : /*#__PURE__*/React.createElement("span", null, p.photo || p.name));
  }
  const Check = /*#__PURE__*/React.createElement("svg", {
    className: "ck",
    width: "16",
    height: "16",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.6",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("polyline", {
    points: "20 6 9 17 4 12"
  }));
  const ShipIcon = /*#__PURE__*/React.createElement("svg", {
    width: "15",
    height: "15",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M3 7h11v8H3z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14 10h4l3 3v2h-7z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "7",
    cy: "17",
    r: "1.6"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "17",
    cy: "17",
    r: "1.6"
  }));
  const GuardIcon = /*#__PURE__*/React.createElement("svg", {
    width: "15",
    height: "15",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 3l7 3v5c0 4.6-3 7.8-7 9-4-1.2-7-4.4-7-9V6l7-3z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9 12l2 2 4-4"
  }));
  const CycleIcon = /*#__PURE__*/React.createElement("svg", {
    width: "15",
    height: "15",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M4 12a8 8 0 0 1 13.7-5.6L20 9"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20 4v5h-5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20 12a8 8 0 0 1-13.7 5.6L4 15"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4 20v-5h5"
  }));
  const BENEFIT_ICONS = {
    manure: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 3c2.5 3 4 5 4 7a4 4 0 0 1-8 0c0-2 1.5-4 4-7z"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M6 20h12"
    })),
    nutrition: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 3v18"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M8 6c-2 0-3 1.5-3 3.5S6.5 13 8 13"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M16 6c2 0 3 1.5 3 3.5S17.5 13 16 13"
    })),
    coat: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "12",
      cy: "12",
      r: "3.2"
    })),
    gut: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M6 4v6a6 6 0 0 0 12 0"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M18 10v4a4 4 0 0 1-8 0v-1a3 3 0 0 0-6 0v3"
    })),
    shield: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 3l7 3v5c0 4.6-3 7.8-7 9-4-1.2-7-4.4-7-9V6l7-3z"
    })),
    joint: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M8 5a3 3 0 0 1 0 6M16 19a3 3 0 0 1 0-6M8.5 8.5l7 7"
    })),
    muscle: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M4 10c3 0 4-3 7-3 4 0 5 4 5 7s-2 3-4 3-2-2-4-2-3 2-5 0"
    })),
    bone: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M14.8 4.6a2.1 2.1 0 0 1 3 3 2.1 2.1 0 0 1-.5 3.3l-6.4 6.4A2.1 2.1 0 0 1 7 20.4a2.1 2.1 0 0 1-3-3 2.1 2.1 0 0 1 .5-3.3l6.4-6.4A2.1 2.1 0 0 1 14.8 4.6z"
    })),
    drop: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 3.5s6 5.6 6 10.1a6 6 0 0 1-12 0c0-4.5 6-10.1 6-10.1z"
    })),
    moon: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M20.5 13.2A8 8 0 1 1 11 3.4a6.2 6.2 0 0 0 9.5 9.8z"
    })),
    activity: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("polyline", {
      points: "3 12 7 12 10 5 14 19 17 12 21 12"
    })),
    trend: /*#__PURE__*/React.createElement("svg", {
      width: "17",
      height: "17",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("polyline", {
      points: "4 17 9 11 13 14 20 6"
    }), /*#__PURE__*/React.createElement("polyline", {
      points: "15 6 20 6 20 11"
    }))
  };

  /* ---------------- Curated card ---------------- */
  function BundleCard({
    b,
    mode,
    onAdd,
    onCustomize
  }) {
    const cart = Object.fromEntries(b.items.map(k => [k, 1]));
    const pr = priceCart(cart, mode);
    return /*#__PURE__*/React.createElement("article", {
      className: 'bx-bundle' + (b.feature ? ' feature' : ''),
      style: {
        position: 'relative'
      }
    }, b.tag && /*#__PURE__*/React.createElement("span", {
      className: "bx-bundle-tag"
    }, b.tag), /*#__PURE__*/React.createElement("div", {
      className: "bx-bundle-media"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-chip-stack"
    }, b.items.map(k => /*#__PURE__*/React.createElement("div", {
      className: "pc",
      key: k
    }, BY_KEY[k].img ? /*#__PURE__*/React.createElement("img", {
      src: BY_KEY[k].img,
      alt: BY_KEY[k].name
    }) : /*#__PURE__*/React.createElement("span", null, BY_KEY[k].name))))), /*#__PURE__*/React.createElement("div", {
      className: "bx-bundle-body"
    }, /*#__PURE__*/React.createElement("h3", null, b.name), /*#__PURE__*/React.createElement("div", {
      className: "bx-bundle-for"
    }, b.forwho), /*#__PURE__*/React.createElement("p", {
      className: "bx-bundle-desc"
    }, b.desc), /*#__PURE__*/React.createElement("ul", {
      className: "bx-bundle-includes"
    }, b.items.map(k => /*#__PURE__*/React.createElement("li", {
      key: k
    }, Check, /*#__PURE__*/React.createElement("span", null, BY_KEY[k].name), /*#__PURE__*/React.createElement("span", {
      style: {
        color: 'var(--muted)',
        fontWeight: 500
      }
    }, "· ", BY_KEY[k].cat)))), /*#__PURE__*/React.createElement("div", {
      className: "bx-bundle-foot"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-price-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-price-now"
    }, money(pr.total)), /*#__PURE__*/React.createElement("span", {
      className: "bx-price-was"
    }, money(pr.retail)), pr.pct > 0 && /*#__PURE__*/React.createElement("span", {
      className: "bx-price-save"
    }, "Save ", Math.round(pr.pct * 100), "%")), /*#__PURE__*/React.createElement("div", {
      className: "bx-price-sub"
    }, mode === 'sub' ? 'Subscribe & Save · ships every 90 days' : 'One-time purchase'), /*#__PURE__*/React.createElement("div", {
      className: "bx-bundle-cta"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bx-btn bx-btn-primary",
      onClick: () => onAdd(b)
    }, "Add bundle"), /*#__PURE__*/React.createElement("button", {
      className: "bx-btn bx-btn-ghost",
      onClick: () => onCustomize(b)
    }, "Customize")))));
  }

  /* ---------------- Catalog product tile (compact, clickable) ---------------- */
  function ProductRow({
    p,
    qty,
    open,
    onAdd,
    onQty,
    onOpen,
    mode
  }) {
    const price = mode === 'sub' ? round2(p.msrp * (1 - SUB_DISCOUNT)) : p.msrp;
    return /*#__PURE__*/React.createElement("div", {
      className: "bx-prod",
      "data-key": p.key,
      "data-in": qty > 0,
      "data-open": open,
      role: "button",
      tabIndex: 0,
      onClick: () => onOpen(open ? null : p.key),
      onKeyDown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(open ? null : p.key);
        }
      }
    }, /*#__PURE__*/React.createElement(ProductMedia, {
      p: p,
      cls: "bx-prod-media"
    }), /*#__PURE__*/React.createElement("div", {
      className: "bx-prod-info"
    }, /*#__PURE__*/React.createElement("h4", null, p.name), /*#__PURE__*/React.createElement("div", {
      className: "bx-prod-cat"
    }, p.cat), /*#__PURE__*/React.createElement("div", {
      className: "bx-prod-pricerow"
    }, /*#__PURE__*/React.createElement("span", {
      className: "now"
    }, money(price)), mode === 'sub' && /*#__PURE__*/React.createElement("span", {
      className: "was"
    }, money(p.msrp)))), /*#__PURE__*/React.createElement("div", {
      className: "bx-prod-action",
      onClick: e => e.stopPropagation()
    }, qty > 0 ? /*#__PURE__*/React.createElement("div", {
      className: "bx-step"
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => onQty(p.key, qty - 1),
      "aria-label": "Decrease"
    }, "–"), /*#__PURE__*/React.createElement("span", null, qty), /*#__PURE__*/React.createElement("button", {
      className: "plus",
      onClick: () => onQty(p.key, qty + 1),
      "aria-label": "Increase"
    }, "+")) : /*#__PURE__*/React.createElement("button", {
      className: "bx-step-add",
      onClick: () => onAdd(p.key),
      "aria-label": 'Add ' + p.name
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-step-add-plus"
    }, "+"), "Add")));
  }

  /* ---------------- Protocol detail (reveals below the catalog on click) ---------------- */
  function ProductDetail({
    p,
    qty,
    mode,
    onAdd,
    onQty,
    cart
  }) {
    if (!p) return null;
    const price = mode === 'sub' ? round2(p.msrp * (1 - SUB_DISCOUNT)) : p.msrp;
    // mobile foot (mock frame B): cost of adding one more + the extra savings it unlocks
    let addCost = 0,
      addSave = 0;
    if (cart) {
      const t0 = priceCart(cart, mode);
      const c1 = Object.assign({}, cart);
      c1[p.key] = (c1[p.key] || 0) + 1;
      const t1 = priceCart(c1, mode);
      addCost = round2(t1.total - t0.total);
      addSave = round2(t1.save - t0.save);
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "bx-detail",
      key: p.key
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-left"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-media"
    }, p.img ? /*#__PURE__*/React.createElement("img", {
      src: p.img,
      alt: p.name
    }) : /*#__PURE__*/React.createElement("span", null, p.photo || p.name)), /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-side"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-price"
    }, /*#__PURE__*/React.createElement("span", {
      className: "now"
    }, money(price)), mode === 'sub' && /*#__PURE__*/React.createElement("span", {
      className: "was"
    }, money(p.msrp))), qty > 0 ? /*#__PURE__*/React.createElement("div", {
      className: "bx-step bx-step-wide"
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => onQty(p.key, qty - 1),
      "aria-label": "Decrease"
    }, "–"), /*#__PURE__*/React.createElement("span", null, qty), /*#__PURE__*/React.createElement("button", {
      className: "plus",
      onClick: () => onQty(p.key, qty + 1),
      "aria-label": "Increase"
    }, "+")) : /*#__PURE__*/React.createElement("button", {
      className: "bx-btn bx-btn-primary bx-detail-add",
      onClick: () => onAdd(p.key)
    }, "Add to bundle +"))), /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-cat"
    }, p.cat), /*#__PURE__*/React.createElement("h3", null, p.name), /*#__PURE__*/React.createElement("p", {
      className: "bx-detail-lede"
    }, p.desc), /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-meta"
    }, /*#__PURE__*/React.createElement("div", {
      className: p.bestForTip ? 'has-tip' : undefined,
      tabIndex: p.bestForTip ? 0 : undefined
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, "Best for"), /*#__PURE__*/React.createElement("span", {
      className: "v"
    }, p.bestFor.split(',')[0]), p.bestForNote && /*#__PURE__*/React.createElement("span", {
      className: "v2 v2-desk"
    }, p.bestForNote), p.bestForTip && /*#__PURE__*/React.createElement("span", {
      className: "bx-tip",
      role: "tooltip"
    }, p.bestForTip)), /*#__PURE__*/React.createElement("div", {
      className: p.resultsTip ? 'has-tip' : undefined,
      tabIndex: p.resultsTip ? 0 : undefined
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, /*#__PURE__*/React.createElement("span", {
      className: "k-d"
    }, "Initial Results"), /*#__PURE__*/React.createElement("span", {
      className: "k-m"
    }, "Start seeing results")), /*#__PURE__*/React.createElement("span", {
      className: "v"
    }, p.results || '7–14 days'), p.resultsNote && /*#__PURE__*/React.createElement("span", {
      className: "v2 v2-desk"
    }, p.resultsNote), p.resultsTip && /*#__PURE__*/React.createElement("span", {
      className: "bx-tip",
      role: "tooltip"
    }, p.resultsTip)), /*#__PURE__*/React.createElement("div", {
      className: p.supplyTip ? 'has-tip' : undefined,
      tabIndex: p.supplyTip ? 0 : undefined
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, "Size"), /*#__PURE__*/React.createElement("span", {
      className: "v"
    }, p.unit.split('·')[0].trim(), p.supply && /*#__PURE__*/React.createElement("span", {
      className: "v-sup"
    }, " · ", p.supplyShort || p.supply)), p.supply && /*#__PURE__*/React.createElement("span", {
      className: "v2"
    }, /*#__PURE__*/React.createElement("span", {
      className: "v2-d"
    }, p.supplyShort || p.supply), p.supplyMobile && /*#__PURE__*/React.createElement("span", {
      className: "v2-m"
    }, "(", p.supply, ")")), p.supplyTip && /*#__PURE__*/React.createElement("span", {
      className: "bx-tip",
      role: "tooltip"
    }, p.supplyTip))), (p.bestForTip || p.resultsTip || p.supplyTip) && /*#__PURE__*/React.createElement("span", {
      className: "bx-meta-hint"
    }, "Corners open more details"), /*#__PURE__*/React.createElement("ul", {
      className: "bx-detail-benefits"
    }, p.benefits.map(b => {
      const text = typeof b === 'string' ? b : b.text;
      const sub = typeof b === 'object' ? b.sub : null;
      const ic = typeof b === 'object' && b.icon ? BENEFIT_ICONS[b.icon] : Check;
      return /*#__PURE__*/React.createElement("li", {
        key: text
      }, ic, /*#__PURE__*/React.createElement("span", {
        className: "bx-bene-tx"
      }, /*#__PURE__*/React.createElement("b", null, text), sub && /*#__PURE__*/React.createElement("em", null, sub)));
    })), /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-actives"
    }, p.actives.map(a => /*#__PURE__*/React.createElement("span", {
      className: "chip",
      key: a
    }, a))), p.pairs && p.pairs.length > 0 && (() => {
      const list = p.pairs.filter(k => k !== p.key);
      const top = list.slice(0, 3),
        bottom = list.slice(3);
      const chip = k => /*#__PURE__*/React.createElement("button", {
        className: "pairchip",
        key: k,
        onClick: () => onAdd(k)
      }, "+ ", BY_KEY[k].name);
      return /*#__PURE__*/React.createElement("div", {
        className: "bx-detail-pairs"
      }, /*#__PURE__*/React.createElement("span", {
        className: "lab"
      }, /*#__PURE__*/React.createElement("span", {
        className: "lab-full"
      }, p.pairsLabel || 'Pairs well with'), /*#__PURE__*/React.createElement("span", {
        className: "lab-short"
      }, "Pairs well with")), /*#__PURE__*/React.createElement("div", {
        className: "rows"
      }, /*#__PURE__*/React.createElement("div", {
        className: "row"
      }, top.map(chip)), bottom.length > 0 && /*#__PURE__*/React.createElement("div", {
        className: "row"
      }, bottom.map(chip))));
    })(), /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-mfoot"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pdprice"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pdcost"
    }, money(addCost)), /*#__PURE__*/React.createElement("span", {
      className: "pddelta"
    }, "saves an additional ", /*#__PURE__*/React.createElement("b", null, money(addSave)))), /*#__PURE__*/React.createElement("button", {
      className: "pdadd",
      onClick: () => onAdd(p.key)
    }, "Add"))));
  }

  /* ---------------- Supply calculator (separate card under the detail) ---------------- */
  function SupplyCalculator({
    p,
    cadence,
    horses,
    setHorses,
    feed,
    setFeed,
    onQty
  }) {
    if (!p) return null;
    const unitWord = /bucket/i.test(p.unit) ? 'bucket' : 'gallon';
    const baseDays = p.daysPerUnit || 30; // days one unit lasts: 1 horse, maintenance
    const perHerdDays = baseDays / (horses * feed);
    const unitsPerShip = Math.max(1, Math.ceil(parseInt(cadence, 10) * horses * feed / baseDays));
    return /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-head"
    }, /*#__PURE__*/React.createElement("svg", {
      width: "14",
      height: "14",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("rect", {
      x: "4",
      y: "2",
      width: "16",
      height: "20",
      rx: "2"
    }), /*#__PURE__*/React.createElement("line", {
      x1: "8",
      y1: "7",
      x2: "16",
      y2: "7"
    }), /*#__PURE__*/React.createElement("line", {
      x1: "8",
      y1: "11",
      x2: "10",
      y2: "11"
    }), /*#__PURE__*/React.createElement("line", {
      x1: "8",
      y1: "15",
      x2: "10",
      y2: "15"
    })), "Supply calculator ", /*#__PURE__*/React.createElement("span", {
      className: "bx-calc-for"
    }, "for ", p.name)), /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-controls"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-field"
    }, /*#__PURE__*/React.createElement("label", null, "How many horses?"), /*#__PURE__*/React.createElement("div", {
      className: "bx-step bx-step-wide"
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => setHorses(Math.max(1, horses - 1)),
      "aria-label": "Fewer horses"
    }, "–"), /*#__PURE__*/React.createElement("span", null, horses), /*#__PURE__*/React.createElement("button", {
      className: "plus",
      onClick: () => setHorses(horses + 1),
      "aria-label": "More horses"
    }, "+"))), /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-field"
    }, /*#__PURE__*/React.createElement("label", null, "How much per horse?"), /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-seg"
    }, /*#__PURE__*/React.createElement("button", {
      "data-on": feed === 1,
      onClick: () => setFeed(1)
    }, "Maintenance"), /*#__PURE__*/React.createElement("button", {
      "data-on": feed === 2,
      onClick: () => setFeed(2)
    }, "Performance 2×")))), /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-result"
    }, /*#__PURE__*/React.createElement("div", {
      className: "r-line"
    }, "One ", unitWord, " lasts ", /*#__PURE__*/React.createElement("b", null, "~", Math.round(perHerdDays), " days"), " for ", horses, " ", horses > 1 ? 'horses' : 'horse', "."), /*#__PURE__*/React.createElement("div", {
      className: "r-big"
    }, unitsPerShip, " ", unitWord, unitsPerShip > 1 ? 's' : '', " ", /*#__PURE__*/React.createElement("span", null, "every ", cadence, " days")), /*#__PURE__*/React.createElement("button", {
      className: "bx-btn bx-btn-primary",
      onClick: () => onQty(p.key, unitsPerShip)
    }, "Set quantity to ", unitsPerShip))));
  }

  /* ---------------- Liquid vs powder/pellets accordion ---------------- */
  const LIQUID_ADV = [{
    q: 'Delivered, not wasted',
    a: 'Every 100X formula is a liquid delivery system. Every active ingredient is fully dissolved, evenly dispersed, and ready to go to work the moment it reaches the gut wall. A powder or pellet still has a job to do first: it has to break apart, hydrate, and dissolve inside a moving digestive tract, and a horse\u2019s gut doesn\u2019t wait for it to catch up. Whatever never dissolves passes straight through, which is why you can find yesterday\u2019s supplement in the manure pile. You paid for a full dose. Your horse got a fraction of it. With liquid, what\u2019s on the label is delivered, not left in the bucket.'
  }, {
    q: 'Nothing left in the bucket',
    a: 'Liquid coats the feed so every bite carries the dose. Powders and pellets get nosed to the bottom of the pan and left behind, especially by picky horses.'
  }, {
    q: 'The same exact dose daily',
    a: 'A measured pour delivers identical, concentrated servings every feeding. Scooped powders settle and vary, and pellets are mostly carrier with a little active dusted on.'
  }, {
    q: 'No fillers, no binders',
    a: 'Pellets need starch and binders just to hold their shape. Our liquid is concentrated active ingredients, with nothing added to bulk it up.'
  }, {
    q: 'Consistent, feeding to feeding',
    a: 'No dust, no settling, no sifting out in the feed pan. The formula stays uniform from the first serving to the last.'
  }];
  function LiquidAdvantage() {
    const [open, setOpen] = useState(0);
    return /*#__PURE__*/React.createElement("div", {
      className: "bx-adv"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-adv-eyebrow"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }), "Why liquid"), /*#__PURE__*/React.createElement("h3", {
      className: "bx-adv-h"
    }, "The liquid advantage"), /*#__PURE__*/React.createElement("p", {
      className: "bx-adv-sub"
    }, "Why our liquid formulas outperform powders and pellets."), /*#__PURE__*/React.createElement("div", {
      className: "bx-adv-list"
    }, LIQUID_ADV.map((r, i) => /*#__PURE__*/React.createElement("div", {
      className: "bx-adv-row",
      key: i,
      "data-open": open === i ? 'true' : 'false'
    }, /*#__PURE__*/React.createElement("button", {
      className: "bx-adv-head",
      onClick: () => setOpen(open === i ? -1 : i),
      "aria-expanded": open === i
    }, /*#__PURE__*/React.createElement("span", {
      className: "q"
    }, r.q), /*#__PURE__*/React.createElement("span", {
      className: "ic",
      "aria-hidden": "true"
    })), /*#__PURE__*/React.createElement("div", {
      className: "bx-adv-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "inner"
    }, /*#__PURE__*/React.createElement("p", null, r.a)))))));
  }

  /* ---------------- Summary rail (gamified) ---------------- */
  function Summary({
    cart,
    mode,
    setMode,
    cadence,
    setCadence,
    onQty,
    onRemove
  }) {
    const sub = priceCart(cart, 'sub');
    const one = priceCart(cart, 'one');
    const pr = mode === 'sub' ? sub : one;
    const hasGut = (cart['gut-x'] || 0) > 0;
    const nextStatus = VOL_STATUS.find(s => s.spend > pr.total) || null;
    const curStatus = VOL_STATUS.slice().reverse().find(s => pr.total >= s.spend) || VOL_STATUS[0];
    const animTotal = useAnimatedNumber(pr.total);
    const animSub = useAnimatedNumber(sub.total);
    const animSave = useAnimatedNumber(pr.save);
    const animPerUnit = useAnimatedNumber(pr.perUnit);
    const subSaves = one.total - sub.total; // how much subscribing beats one-time
    const empty = pr.keys.length === 0;

    // celebrate when savings increase
    const prevSave = usePrevious(pr.save);
    const [flash, setFlash] = useState(null);
    React.useEffect(() => {
      if (prevSave === undefined || pr.save <= prevSave + 0.005) return;
      setFlash({
        id: Date.now()
      });
      const t = setTimeout(() => setFlash(null), 1500);
      return () => clearTimeout(t);
    }, [pr.save]);
    if (empty) {
      // deepest achievable discount, computed from the engine: best per-product
      // volume tier (max qty) with Subscribe & Save stacked on top. = 40.27% today
      // (Gut X / Joint Flex / Max Calm 4-gal 25.34% one-time × 20% sub).
      const maxPct = (() => {
        let best = 0;
        for (const p of PRODUCTS) {
          const q = (p.tiers || []).reduce((m, t) => Math.max(m, t.q), 1);
          const off = 1 - productPrice(p, q) / (p.msrp * q) * (1 - SUB_DISCOUNT);
          if (off > best) best = off;
        }
        return Math.floor(best * 100);
      })();
      const steps = VOL_STATUS.length;
      return /*#__PURE__*/React.createElement("aside", {
        className: "bx-summary bx-summary-empty"
      }, /*#__PURE__*/React.createElement("div", {
        className: "bx-empty"
      }, /*#__PURE__*/React.createElement("div", {
        className: "bx-empty-h"
      }, "Buy More. Save More."), /*#__PURE__*/React.createElement("span", {
        className: "bx-empty-rule",
        "aria-hidden": "true"
      }), /*#__PURE__*/React.createElement("p", {
        className: "bx-empty-p"
      }, "Add protocols and subscribe. Your savings grow automatically as the bundle grows.")), /*#__PURE__*/React.createElement(LiquidAdvantage, null));
    }
    const subPct = Math.round((sub.retail - sub.total) / Math.max(sub.retail, 1) * 100);
    const onePct = Math.round((one.retail - one.total) / Math.max(one.retail, 1) * 100);

    // split the headline savings into its two sources for the ledger
    const subSave = pr.subSavePart; // extra Subscribe & Save discount (sub mode only)
    const volSave = pr.volSavePart; // volume break vs buying singles (both modes)
    const subSavePct = Math.round(subSave / Math.max(pr.retail, 1) * 100);
    const volSavePct = Math.round(volSave / Math.max(pr.retail, 1) * 100);
    // status bar — milestones gated by NET revenue to the company (amount paid)
    const lastSpend = VOL_STATUS[VOL_STATUS.length - 1].spend;
    const scaleMax = lastSpend * 1.15;
    const markPos = s => Math.min(100, s / scaleMax * 100);
    const fillPct = Math.min(95, Math.max(pr.total > 0 ? 12 : 0, markPos(pr.total)));
    const moreToNext = nextStatus ? nextStatus.spend - pr.total : 0;
    const reward = REWARDS.slice().reverse().find(r => pr.save >= r.at) || null;
    return /*#__PURE__*/React.createElement("aside", {
      className: "bx-summary bx-buybox"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-sum-title"
    }, "Your bundle", /*#__PURE__*/React.createElement("span", {
      className: "bx-sum-count"
    }, pr.distinct)), /*#__PURE__*/React.createElement("ul", {
      className: "bx-sum-lines"
    }, pr.keys.map(k => {
      const p = BY_KEY[k];
      const price = pr.lines[k];
      return /*#__PURE__*/React.createElement("li", {
        className: "bx-sum-line",
        key: k
      }, /*#__PURE__*/React.createElement("div", {
        className: "bx-miniqty"
      }, /*#__PURE__*/React.createElement("button", {
        onClick: () => onQty(k, cart[k] - 1),
        "aria-label": "Decrease"
      }, "–"), /*#__PURE__*/React.createElement("span", null, cart[k]), /*#__PURE__*/React.createElement("button", {
        onClick: () => onQty(k, cart[k] + 1),
        "aria-label": "Increase"
      }, "+")), /*#__PURE__*/React.createElement("div", {
        className: "nm"
      }, /*#__PURE__*/React.createElement("b", null, p.name), /*#__PURE__*/React.createElement("small", null, p.cat)), /*#__PURE__*/React.createElement("span", {
        className: "lp"
      }, money(price)), /*#__PURE__*/React.createElement("button", {
        className: "rm",
        onClick: () => onRemove(k),
        "aria-label": 'Remove ' + p.name
      }, "×"));
    })), pr.save > 0.005 && /*#__PURE__*/React.createElement("div", {
      className: "bx-savings"
    }, flash && /*#__PURE__*/React.createElement("div", {
      className: "bx-unlock",
      key: flash.id
    }, "★ You saved more"), /*#__PURE__*/React.createElement("div", {
      className: "bx-savings-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "lab"
    }, "You’re saving", /*#__PURE__*/React.createElement("br", null), Math.round(pr.pct * 100), "% off"), /*#__PURE__*/React.createElement("span", {
      className: "amt",
      key: Math.round(pr.save)
    }, money(animSave))), false && /*#__PURE__*/React.createElement("div", {
      className: "bx-ledger"
    }, false && mode === 'sub' && subSave > 0.005 && /*#__PURE__*/React.createElement("div", {
      className: "bx-ledger-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ll"
    }, "Subscription ", /*#__PURE__*/React.createElement("em", null, "−", subSavePct, "%")), /*#__PURE__*/React.createElement("span", {
      className: "lv"
    }, "−", money(subSave))), false && volSave > 0.005 && /*#__PURE__*/React.createElement("div", {
      className: "bx-ledger-row",
      "data-grow": true
    }, /*#__PURE__*/React.createElement("span", {
      className: "ll"
    }, "Volume ", /*#__PURE__*/React.createElement("span", {
      className: "cnt"
    }, "(", pr.units, " ", pr.units === 1 ? 'gallon' : 'gallons', ")"), " ", /*#__PURE__*/React.createElement("em", null, "−", volSavePct, "%")), /*#__PURE__*/React.createElement("span", {
      className: "lv"
    }, "−", money(volSave)))), false && /*#__PURE__*/React.createElement("div", {
      className: "bx-tiernext"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-tiernext-track"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-tiernext-fill",
      style: {
        width: fillPct + '%'
      }
    }), VOL_STATUS.filter(s => s.spend > 0).map(s => {
      const state = pr.total >= s.spend ? 'hit' : nextStatus && s.spend === nextStatus.spend ? 'next' : 'locked';
      return /*#__PURE__*/React.createElement("div", {
        className: "bx-tiernext-mark",
        key: s.spend,
        "data-state": state,
        style: {
          left: markPos(s.spend) + '%'
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "node"
      }, /*#__PURE__*/React.createElement("svg", {
        viewBox: "0 0 20 20",
        fill: "currentColor",
        "aria-hidden": "true"
      }, /*#__PURE__*/React.createElement("rect", {
        x: "3.4",
        y: "8.2",
        width: "13.2",
        height: "8.8",
        rx: "1.4"
      }), /*#__PURE__*/React.createElement("rect", {
        x: "2.2",
        y: "5.7",
        width: "15.6",
        height: "3.1",
        rx: "1.1"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "7.4",
        cy: "4.4",
        r: "2.3"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "12.6",
        cy: "4.4",
        r: "2.3"
      }))), /*#__PURE__*/React.createElement("span", {
        className: "nm"
      }, s.short));
    })), /*#__PURE__*/React.createElement("div", {
      className: "bx-tiernext-cap"
    }, reward ? /*#__PURE__*/React.createElement(React.Fragment, null, "You’ve saved ", /*#__PURE__*/React.createElement("b", null, money(pr.save)), ", enough for ", /*#__PURE__*/React.createElement("b", null, reward.item), ".") : /*#__PURE__*/React.createElement(React.Fragment, null, "Keep stacking, your savings add up fast.")))), /*#__PURE__*/React.createElement("div", {
      className: "bx-field"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-field-lab"
    }, /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "1."), " Select purchase type"), /*#__PURE__*/React.createElement("button", {
      className: "bx-ptype",
      "data-on": mode === 'sub',
      onClick: () => setMode('sub')
    }, /*#__PURE__*/React.createElement("span", {
      className: "radio"
    }), /*#__PURE__*/React.createElement("div", {
      className: "bx-ptype-main"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-ptype-top"
    }, /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, "Subscribe & Save"), subPct > 0 && /*#__PURE__*/React.createElement("span", {
      className: "savepill"
    }, "Save ", subPct, "%")), /*#__PURE__*/React.createElement("ul", {
      className: "bx-ptype-perks"
    }, /*#__PURE__*/React.createElement("li", null, "Free shipping"), /*#__PURE__*/React.createElement("li", null, "90-day money-back"), hasGut && /*#__PURE__*/React.createElement("li", {
      className: "coral"
    }, "$20,000 Colic Surgery Coverage"), /*#__PURE__*/React.createElement("li", null, "Pause, change, or cancel anytime"))), /*#__PURE__*/React.createElement("div", {
      className: "bx-ptype-price"
    }, /*#__PURE__*/React.createElement("span", {
      className: "was"
    }, money(sub.retail)), /*#__PURE__*/React.createElement("span", {
      className: "now",
      key: Math.round(sub.total)
    }, money(animSub)))), /*#__PURE__*/React.createElement("button", {
      className: "bx-ptype",
      "data-on": mode === 'one',
      onClick: () => setMode('one')
    }, /*#__PURE__*/React.createElement("span", {
      className: "radio"
    }), /*#__PURE__*/React.createElement("div", {
      className: "bx-ptype-main"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-ptype-top"
    }, /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, "One-time purchase")), /*#__PURE__*/React.createElement("ul", {
      className: "bx-ptype-perks"
    }, /*#__PURE__*/React.createElement("li", null, "Ships once. No Colic Surgery Coverage."))), /*#__PURE__*/React.createElement("div", {
      className: "bx-ptype-price"
    }, onePct > 0 && /*#__PURE__*/React.createElement("span", {
      className: "was"
    }, money(one.retail)), /*#__PURE__*/React.createElement("span", {
      className: "now"
    }, money(one.total))))), mode === 'sub' && /*#__PURE__*/React.createElement("div", {
      className: "bx-field"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-field-lab"
    }, /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "2."), " Delivery time ", /*#__PURE__*/React.createElement("span", {
      className: "muted"
    }, "· every ", cadence, " days")), /*#__PURE__*/React.createElement("div", {
      className: "bx-cadence-seg"
    }, [['60', 'Top-up'], ['90', 'Recommended'], ['120', 'Stock up']].map(([v, l]) => /*#__PURE__*/React.createElement("button", {
      key: v,
      "data-on": cadence === v,
      onClick: () => setCadence(v)
    }, v, " days", /*#__PURE__*/React.createElement("span", {
      className: "sub"
    }, l))))), /*#__PURE__*/React.createElement("button", {
      className: "bx-checkout",
      onClick: () => {
        // Was: location.href = 'Checkout.html' — a static file from the design mockup, so the
        // builder could never actually sell anything. Now hands the chosen cover to
        // gx-bundle-checkout.js, which composes real variants + selling plans and adds to cart.
        if (pr.keys.length && window.GXBundleCheckout) window.GXBundleCheckout(cart, mode, cadence);
      }
    }, /*#__PURE__*/React.createElement("span", null, mode === 'sub' ? 'Subscribe & Save' : 'Checkout'), /*#__PURE__*/React.createElement("span", {
      className: "cp"
    }, money(animTotal))), /*#__PURE__*/React.createElement("div", {
      className: "bx-pay"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bx-pay-shop",
      "aria-label": "Shop Pay"
    }, "shop", /*#__PURE__*/React.createElement("b", null, "Pay")), /*#__PURE__*/React.createElement("button", {
      className: "bx-pay-pp",
      "aria-label": "PayPal"
    }, /*#__PURE__*/React.createElement("b", null, "Pay"), "Pal")), /*#__PURE__*/React.createElement("div", {
      className: "bx-paytrust"
    }, /*#__PURE__*/React.createElement("span", null, ShipIcon, "Free shipping"), /*#__PURE__*/React.createElement("span", null, GuardIcon, "90-day money-back"), /*#__PURE__*/React.createElement("span", null, CycleIcon, "Cancel anytime")));
  }

  /* ---------------- Barn plan wizard (ask → recommend → fill cart) ---------------- */
  function PlanGlyph({
    p
  }) {
    return /*#__PURE__*/React.createElement("svg", {
      width: "18",
      height: "18",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      dangerouslySetInnerHTML: {
        __html: PROD_ICONS[p.icon] || ''
      }
    });
  }
  function PlanMini({
    value,
    onChange,
    max
  }) {
    return /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-mini"
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => onChange(Math.max(0, value - 1)),
      "aria-label": "Fewer"
    }, "–"), /*#__PURE__*/React.createElement("span", null, value), /*#__PURE__*/React.createElement("button", {
      onClick: () => onChange(max == null ? value + 1 : Math.min(max, value + 1)),
      "aria-label": "More"
    }, "+"));
  }
  function BarnPlanner({
    onApply
  }) {
    const [step, setStep] = useState(1);
    const [barn, setBarn] = useState(3);
    const [picked, setPicked] = useState([]);
    const [levels, setLevels] = useState({});
    const [events, setEvents] = useState(4); // show / travel / weather events a year

    // when barn size changes, re-sync every picked protocol to the new count
    React.useEffect(() => {
      setLevels(l => {
        const m = {};
        Object.keys(l).forEach(k => {
          const perf = Math.min(l[k].perf || 0, barn);
          m[k] = {
            perf,
            maint: barn - perf
          };
        });
        return m;
      });
    }, [barn]);
    const toggle = k => {
      setPicked(p => {
        if (p.includes(k)) {
          setLevels(l => {
            const m = {
              ...l
            };
            delete m[k];
            return m;
          });
          return p.filter(x => x !== k);
        }
        setLevels(l => ({
          ...l,
          [k]: l[k] || {
            maint: barn,
            perf: 0
          }
        }));
        return [...p, k];
      });
    };
    // maintenance + performance always sum to the barn size: moving one moves the other
    const setLvl = (k, field, val) => setLevels(l => {
      const cur = l[k] || {
        maint: barn,
        perf: 0
      };
      const v = Math.max(0, Math.min(barn, val));
      const other = field === 'maint' ? 'perf' : 'maint';
      return {
        ...l,
        [k]: {
          [field]: v,
          [other]: barn - v
        }
      };
    });

    // recommended auto-ship cart from the answers
    const planCart = {};
    picked.forEach(k => {
      const lv = levels[k] || {
        maint: barn,
        perf: 0
      };
      const weighted = lv.maint + 2 * lv.perf; // performance/loading = 2x consumption
      if (weighted <= 0) return;
      const days = BY_KEY[k].daysPerUnit || 30;
      planCart[k] = Math.max(1, Math.ceil(90 * weighted / days));
    });
    const planKeys = Object.keys(planCart);

    /* Show / travel / weather reserve. Episodic stress (shows, hauling, weather
       swings, moves, weaning) is a ~10-day elevated dose on the stress-sensitive
       protocols only (gut, calm, hydration).
       KEY: every 90-day shipment already ships ~128 days of supply per gallon, so
       each cycle carries a built-in surplus (supply days - days actually consumed).
       That surplus absorbs the first several events for free. We only recommend an
       extra reserve unit once a cycle's event demand exceeds that built-in room. */
    const STRESS_KEYS = ['gut-x', 'max-calm', 'hydra-max'];
    const EVENT_DAYS = 10; // days of elevated dosing per event
    const EVENT_DOSE = 2; // oz/day during an event (the performance/loading rate)
    const EVENT_OZ = EVENT_DAYS * EVENT_DOSE; // = 20 oz of extra product per event, per horse
    const reserveCart = {};
    picked.forEach(k => {
      if (!STRESS_KEYS.includes(k) || events <= 0) return;
      const lv = levels[k] || {
        maint: barn,
        perf: 0
      };
      const affected = lv.maint; // only maintenance horses bump up during stress
      if (affected <= 0) return; // all-performance horses are already at the event rate
      const days = BY_KEY[k].daysPerUnit || 30; // oz per gallon (1 oz/day maintenance)
      const weighted = lv.maint + 2 * lv.perf; // same consumption model as the plan cart
      // ounces already in this cycle's shipment above what gets consumed in 90 days
      const surplusOz = (planCart[k] || 0) * days - weighted * 90;
      const cycleEvents = events * 90 / 365; // events that fall in a 90-day shipment
      const reserveOz = cycleEvents * EVENT_OZ * affected;
      const extraNeeded = reserveOz - Math.max(0, surplusOz);
      const u = extraNeeded > 0 ? Math.ceil(extraNeeded / days) : 0;
      if (u > 0) reserveCart[k] = u;
    });
    const finalCart = {
      ...planCart
    };
    Object.entries(reserveCart).forEach(([k, u]) => {
      finalCart[k] = (finalCart[k] || 0) + u;
    });
    const finalKeys = Object.keys(finalCart);
    const reserveTotal = Object.values(reserveCart).reduce((a, b) => a + b, 0);
    // is there any maintenance horse on a stress protocol? if not, the events slider is meaningless
    const hasStressMaint = picked.some(k => STRESS_KEYS.includes(k) && (levels[k] || {
      maint: barn
    }).maint > 0);
    const pr = priceCart(finalCart, 'sub');
    const Steps = /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-steps"
    }, ['Your barn', 'Protocols', 'Levels'].map((lab, i) => {
      const n = i + 1;
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: lab
      }, i > 0 && /*#__PURE__*/React.createElement("span", {
        className: "bx-plan-rule"
      }), /*#__PURE__*/React.createElement("span", {
        className: 'bx-plan-step' + (step === n ? ' on' : step > n ? ' done' : '')
      }, /*#__PURE__*/React.createElement("span", {
        className: "n"
      }, step > n ? '\u2713' : n), /*#__PURE__*/React.createElement("span", {
        className: "lab"
      }, lab)));
    }));
    return /*#__PURE__*/React.createElement("div", {
      className: "bx-plan"
    }, Steps, step === 1 && /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-q"
    }, /*#__PURE__*/React.createElement("h3", null, "How many horses are in your barn?"), /*#__PURE__*/React.createElement("p", null, "We’ll build a plan sized to your whole barn, not just one horse."), /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-bignum"
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => setBarn(Math.max(1, barn - 1)),
      "aria-label": "Fewer horses"
    }, "–"), /*#__PURE__*/React.createElement("span", {
      className: "v"
    }, barn, /*#__PURE__*/React.createElement("small", null, barn > 1 ? 'horses' : 'horse')), /*#__PURE__*/React.createElement("button", {
      onClick: () => setBarn(barn + 1),
      "aria-label": "More horses"
    }, "+")), /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-nav"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bx-btn bx-btn-primary",
      onClick: () => setStep(2)
    }, "Continue"))), step === 2 && /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-q"
    }, /*#__PURE__*/React.createElement("h3", null, "What does your barn need support with?"), /*#__PURE__*/React.createElement("p", null, "Pick the areas that matter for your horses. We’ll match each to a protocol and size it on the next step."), /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-pick"
    }, PRODUCTS.map(p => {
      const on = picked.includes(p.key);
      return /*#__PURE__*/React.createElement("button", {
        key: p.key,
        className: 'bx-plan-opt' + (on ? ' on' : ''),
        onClick: () => toggle(p.key)
      }, /*#__PURE__*/React.createElement("span", {
        className: "ic",
        style: {
          background: p.accent
        }
      }, /*#__PURE__*/React.createElement(PlanGlyph, {
        p: p
      })), /*#__PURE__*/React.createElement("span", {
        className: "tx"
      }, /*#__PURE__*/React.createElement("span", {
        className: "nm"
      }, p.name), /*#__PURE__*/React.createElement("span", {
        className: "bf"
      }, p.cat)), /*#__PURE__*/React.createElement("span", {
        className: 'chk' + (on ? ' on' : '')
      }, on ? '\u2713' : '+'));
    })), /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-nav"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bx-plan-ghost",
      onClick: () => setStep(1)
    }, "Back"), /*#__PURE__*/React.createElement("button", {
      className: "bx-btn bx-btn-primary",
      disabled: picked.length === 0,
      onClick: () => setStep(3)
    }, "Continue"))), step === 3 && /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-q"
    }, /*#__PURE__*/React.createElement("h3", null, "How many horses are on each level?"), /*#__PURE__*/React.createElement("p", null, "Set how many of your ", barn, " ", barn > 1 ? 'horses' : 'horse', " are on each dose."), /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-lvl"
    }, picked.map(k => {
      const p = BY_KEY[k];
      const lv = levels[k] || {
        maint: barn,
        perf: 0
      };
      const hydra = k === 'hydra-max';
      const maintDose = hydra ? '1 scoop \u00b7 8 oz a day' : '1 pump \u00b7 1 oz a day';
      const perfDose = hydra ? '2 scoops \u00b7 16 oz a day' : '2 pumps \u00b7 2 oz a day';
      const perfLabel = hydra ? 'Performance' : 'Performance / loading';
      return /*#__PURE__*/React.createElement("div", {
        className: "bx-plan-lvlrow",
        key: k
      }, /*#__PURE__*/React.createElement("div", {
        className: "top"
      }, /*#__PURE__*/React.createElement("span", {
        className: "ic",
        style: {
          background: p.accent
        }
      }, /*#__PURE__*/React.createElement(PlanGlyph, {
        p: p
      })), p.name), /*#__PURE__*/React.createElement("div", {
        className: "bx-plan-counts"
      }, /*#__PURE__*/React.createElement("div", {
        className: "bx-plan-count"
      }, /*#__PURE__*/React.createElement("label", null, "Maintenance ", /*#__PURE__*/React.createElement("span", {
        className: "dose"
      }, maintDose)), /*#__PURE__*/React.createElement(PlanMini, {
        value: lv.maint,
        max: barn,
        onChange: v => setLvl(k, 'maint', v)
      })), /*#__PURE__*/React.createElement("div", {
        className: "bx-plan-count"
      }, /*#__PURE__*/React.createElement("label", null, perfLabel, " ", /*#__PURE__*/React.createElement("span", {
        className: "dose"
      }, perfDose)), /*#__PURE__*/React.createElement(PlanMini, {
        value: lv.perf,
        max: barn,
        onChange: v => setLvl(k, 'perf', v)
      }))), hydra && /*#__PURE__*/React.createElement("p", {
        className: "bx-plan-lvlnote"
      }, "Fed daily in 3–4 gallons of water."));
    })), /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-nav"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bx-plan-ghost",
      onClick: () => setStep(2)
    }, "Back"), /*#__PURE__*/React.createElement("button", {
      className: "bx-btn bx-btn-primary",
      onClick: () => setStep(4)
    }, "Build my plan"))), step === 4 && /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-result"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-q",
      style: {
        marginBottom: 4
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-plan-reseyebrow"
    }, "Your barn’s plan"), /*#__PURE__*/React.createElement("h3", null, "Here’s what to put on auto-ship."), /*#__PURE__*/React.createElement("p", null, "Sized for ", barn, " ", barn > 1 ? 'horses' : 'horse', ", shipped every 90 days. Adjust anytime.")), planKeys.length === 0 ? /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-empty"
    }, "Set at least one horse on a protocol to see your plan.") : /*#__PURE__*/React.createElement(React.Fragment, null, hasStressMaint ? /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-reserve"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-reserve-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "lab"
    }, "Show, travel & weather"), /*#__PURE__*/React.createElement("span", {
      className: "val"
    }, events === 0 ? 'None' : events >= 24 ? '24+ a year' : events + ' a year')), /*#__PURE__*/React.createElement("input", {
      className: "bx-plan-slider",
      type: "range",
      min: "0",
      max: "24",
      step: "1",
      style: {
        '--fill': events / 24 * 100 + '%'
      },
      value: events,
      onChange: e => setEvents(parseInt(e.target.value, 10))
    }), /*#__PURE__*/React.createElement("p", {
      className: "bx-plan-reserve-help"
    }, "Shows & hauling, big weather swings, a move or new pasture mates, hard training, injury or extensive stall rest, vet work. We’ll keep a little extra gut, calm, and hydration support on hand for the hard weeks.")) : /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-reserve"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-reserve-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "lab"
    }, "Your dosing")), /*#__PURE__*/React.createElement("ul", {
      className: "bx-plan-confirm"
    }, picked.map(k => {
      const lv = levels[k] || {
        maint: barn,
        perf: 0
      };
      const parts = [];
      if (lv.maint > 0) parts.push(lv.maint + ' on maintenance');
      if (lv.perf > 0) parts.push(lv.perf + ' on performance');
      return /*#__PURE__*/React.createElement("li", {
        key: k
      }, /*#__PURE__*/React.createElement("span", {
        className: "nm"
      }, /*#__PURE__*/React.createElement("span", {
        className: "dot",
        style: {
          background: BY_KEY[k].accent
        }
      }), BY_KEY[k].name), /*#__PURE__*/React.createElement("span", {
        className: "mx"
      }, parts.join('  \u00b7  ')));
    }))), /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-lines"
    }, finalKeys.map(k => {
      const p = BY_KEY[k];
      const u = /bucket/i.test(p.unit) ? 'bucket' : 'gallon';
      const res = reserveCart[k] || 0;
      return /*#__PURE__*/React.createElement("div", {
        className: "bx-plan-line",
        key: k
      }, /*#__PURE__*/React.createElement("span", {
        className: "nm"
      }, /*#__PURE__*/React.createElement("span", {
        className: "ic",
        style: {
          background: p.accent
        }
      }, /*#__PURE__*/React.createElement(PlanGlyph, {
        p: p
      })), p.name), /*#__PURE__*/React.createElement("span", {
        className: "qty"
      }, /*#__PURE__*/React.createElement("span", {
        className: "q"
      }, finalCart[k], " ", u, finalCart[k] > 1 ? 's' : '', " · every 90 days"), res > 0 && /*#__PURE__*/React.createElement("span", {
        className: "resnote"
      }, "includes +", res, " reserve")));
    })), finalKeys.some(k => /bucket/i.test(BY_KEY[k].unit)) && /*#__PURE__*/React.createElement("p", {
      className: "bx-plan-linecap"
    }, "Everything ships together every 90 days. Hydra-MAX comes as 32-serving buckets, so the count is what your barn goes through across the full cycle."), /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-total"
    }, /*#__PURE__*/React.createElement("div", {
      className: "lab"
    }, "Your plan, on subscription"), /*#__PURE__*/React.createElement("div", {
      className: "amt"
    }, money(pr.total), " ", /*#__PURE__*/React.createElement("span", {
      className: "was"
    }, money(pr.retail))), /*#__PURE__*/React.createElement("div", {
      className: "save"
    }, "Save ", money(pr.save), " (", Math.round(pr.pct * 100), "% off) every 90 days"))), /*#__PURE__*/React.createElement("div", {
      className: "bx-plan-nav"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bx-plan-ghost",
      onClick: () => setStep(3)
    }, "Adjust"), /*#__PURE__*/React.createElement("button", {
      className: "bx-btn bx-btn-primary",
      disabled: planKeys.length === 0,
      onClick: () => onApply(finalCart)
    }, "Add this plan to my cart"))));
  }

  /* ---------------- Page ---------------- */
  // Hydrate the cart from the shared handoff (e.g. a PDP's slide-out drawer sends
  // the shopper here via "View full cart & add more"). One cart, two surfaces.
  function readStored(key, fallback, ok) {
    try {
      const v = localStorage.getItem(key);
      return ok(v) ? v : fallback;
    } catch (e) {
      return fallback;
    }
  }
  // ?cart=gut-x:1,osteo-max:1 preloads a cart (deep link from a PDP FAQ / cross-sell).
  // ?mode=sub|one preselects the purchase mode. Both persist via the normal effect below.
  function readUrlCart() {
    try {
      const q = new URLSearchParams(location.search).get('cart');
      if (!q) return null;
      const clean = {};
      q.split(',').forEach(pair => {
        const [k, n] = pair.split(':');
        const key = (k || '').trim();
        const qty = Math.floor(Number(n || 1));
        if (BY_KEY[key] && qty > 0) clean[key] = qty;
      });
      return Object.keys(clean).length ? clean : null;
    } catch (e) {
      return null;
    }
  }
  function readStoredCart() {
    const fromUrl = readUrlCart();
    if (fromUrl) return fromUrl;
    try {
      const raw = JSON.parse(localStorage.getItem('bx_cart') || '{}');
      const clean = {};
      Object.entries(raw).forEach(([k, q]) => {
        if (BY_KEY[k] && q > 0) clean[k] = Math.floor(q);
      });
      return clean;
    } catch (e) {
      return {};
    }
  }
  /* ---------------- Mobile bottom-sheet cart (approved Mobile Cart Redesign, frame B) ---------------- */
  function MobileCartSheet({
    cart,
    mode,
    cadence
  }) {
    // arriving from the PDP drawer's "View full cart & add more" link lands COLLAPSED —
    // the shopper came here to browse/add more, and the peek bar already shows their cart total
    // demo: ?cartopen=1 expands the sheet on load (for design review)
    const [up, setUp] = useState(() => {
      try {
        return new URLSearchParams(location.search).get('cartopen') === '1';
      } catch (e) {
        return false;
      }
    });
    const pr = priceCart(cart, mode);
    const empty = pr.keys.length === 0;
    const pct = Math.round(pr.pct * 100);
    const NEXT = [[300, 'Buckle'], [600, 'Boots'], [900, 'Cowboy Hat'], [1200, 'Barn']];
    const next = NEXT.find(n => pr.total < n[0]);
    return /*#__PURE__*/React.createElement("div", {
      className: 'bx-msheet' + (up ? ' up' : '')
    }, /*#__PURE__*/React.createElement("div", {
      className: "grab"
    }), /*#__PURE__*/React.createElement("div", {
      className: "bar",
      onClick: () => setUp(!up)
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "st"
    }, empty ? 'Your bundle is empty' : pr.units + (pr.units === 1 ? ' item' : ' items') + ' \u00b7 ' + money(pr.total)), /*#__PURE__*/React.createElement("div", {
      className: "ss"
    }, empty ? 'Add protocols to start saving' : 'Saving ' + money(pr.save) + ' \u00b7 ' + pct + '% off')), /*#__PURE__*/React.createElement("button", {
      className: "go",
      disabled: empty,
      onClick: e => {
        e.stopPropagation();
        // Mobile sticky bar. Same handoff as the main checkout button above. `cadence` may not be
        // in this component's scope, so it is read defensively and falls back to the 90-day
        // default the builder itself starts on.
        if (window.GXBundleCheckout) {
          window.GXBundleCheckout(cart, mode, typeof cadence !== 'undefined' ? cadence : '90');
        }
      }
    }, "Checkout")), /*#__PURE__*/React.createElement("div", {
      className: "body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "in"
    }, !empty && /*#__PURE__*/React.createElement("div", {
      className: "mstone"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ml"
    }, /*#__PURE__*/React.createElement("span", null, "Barn status"), /*#__PURE__*/React.createElement("b", null, next ? money(next[0] - pr.total).replace(/\.\d\d$/, '') + ' to ' + next[1] : 'Barn status reached')), /*#__PURE__*/React.createElement("div", {
      className: "track"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fill",
      style: {
        width: Math.min(97, pr.total / 1200 * 97) + '%'
      }
    }), NEXT.map(n => /*#__PURE__*/React.createElement("div", {
      key: n[1],
      className: 'dot' + (pr.total >= n[0] ? ' hit' : ''),
      style: {
        left: n[0] / 1200 * 97 + '%'
      }
    })))), pr.keys.map(k => /*#__PURE__*/React.createElement("div", {
      className: "line",
      key: k
    }, /*#__PURE__*/React.createElement("span", null, BY_KEY[k].name + ' \u00b7 ' + cart[k] + ' ' + (/bucket/i.test(BY_KEY[k].unit) ? 'bucket' : 'gal') + (cart[k] > 1 ? 's' : '') + (mode === 'sub' ? ' \u00b7 every ' + cadence + ' days' : '')), /*#__PURE__*/React.createElement("span", null, money(pr.lines[k])))), !empty && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "hairline"
    }), /*#__PURE__*/React.createElement("div", {
      className: "line mut"
    }, /*#__PURE__*/React.createElement("span", null, "Retail"), /*#__PURE__*/React.createElement("s", null, money(pr.retail))), /*#__PURE__*/React.createElement("div", {
      className: "line save"
    }, /*#__PURE__*/React.createElement("span", null, "You’re saving"), /*#__PURE__*/React.createElement("span", {
      className: "sv"
    }, money(pr.save) + ' (' + pct + '%)')), /*#__PURE__*/React.createElement("div", {
      className: "hairline"
    }), /*#__PURE__*/React.createElement("div", {
      className: "line big"
    }, /*#__PURE__*/React.createElement("span", null, "Subtotal"), /*#__PURE__*/React.createElement("span", null, money(pr.total))), /*#__PURE__*/React.createElement("div", {
      className: "bx-pay"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bx-pay-shop",
      "aria-label": "Shop Pay"
    }, "shop", /*#__PURE__*/React.createElement("b", null, "Pay")), /*#__PURE__*/React.createElement("button", {
      className: "bx-pay-pp",
      "aria-label": "PayPal"
    }, /*#__PURE__*/React.createElement("b", null, "Pay"), "Pal")), /*#__PURE__*/React.createElement("p", {
      className: "micro"
    }, "Free shipping · 90-day money-back · Cancel anytime")))));
  }
  function BundlesPage() {
    const [cart, setCart] = useState(readStoredCart);
    const [mode, setMode] = useState(() => {
      try {
        const m = new URLSearchParams(location.search).get('mode');
        if (m === 'one' || m === 'sub') return m;
      } catch (e) {}
      return readStored('bx_mode', 'sub', v => v === 'one' || v === 'sub');
    });
    const [cadence, setCadence] = useState(() => readStored('bx_cadence', '90', v => ['60', '90', '120'].includes(v)));
    const [openKey, setOpenKey] = useState(() => {
      try {
        if (window.matchMedia('(max-width: 700px)').matches) return null;
      } catch (e) {} // mobile: rows land closed (mock frame B)
      const c = readStoredCart();
      return Object.keys(c)[0] || 'gut-x';
    });
    const [horses, setHorses] = useState(1);
    const [feed, setFeed] = useState(1);

    // keep the shared cart in sync so the round-trip works both ways
    React.useEffect(() => {
      const on = e => {
        if (e.detail && e.detail.src === 'builder') return;
        try {
          const c = JSON.parse(localStorage.getItem('bx_cart') || '{}');
          if (c && typeof c === 'object') setCart(p => JSON.stringify(p) === JSON.stringify(c) ? p : c);
        } catch (err) {}
      };
      window.addEventListener('bx:cart-sync', on);
      return () => window.removeEventListener('bx:cart-sync', on);
    }, []);
    React.useEffect(() => {
      try {
        localStorage.setItem('bx_cart', JSON.stringify(cart));
        localStorage.setItem('bx_mode', mode);
        localStorage.setItem('bx_cadence', cadence);
        window.dispatchEvent(new CustomEvent('bx:cart-sync', {
          detail: {
            src: 'builder'
          }
        }));
      } catch (e) {}
    }, [cart, mode, cadence]);
    const addItem = k => setCart(c => ({
      ...c,
      [k]: (c[k] || 0) + 1
    }));
    const setQty = (k, q) => setCart(c => {
      const n = {
        ...c
      };
      if (q <= 0) delete n[k];else n[k] = q;
      return n;
    });
    const removeItem = k => setCart(c => {
      const n = {
        ...c
      };
      delete n[k];
      return n;
    });
    const addStack = items => {
      setCart(c => {
        const n = {
          ...c
        };
        Object.entries(items).forEach(([k, q]) => {
          n[k] = (n[k] || 0) + q;
        });
        return n;
      });
      const el = document.getElementById('build');
      if (el) window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 16,
        behavior: 'smooth'
      });
    };
    const [calcOpen, setCalcOpen] = useState(false);
    const applyPlan = plan => {
      setCalcOpen(false);
      setMode('sub');
      setCart({
        ...plan
      });
      const el = document.getElementById('build');
      if (el) window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 16,
        behavior: 'smooth'
      });
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "bx-wrap",
      id: "build"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-build-grid"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-build-main"
    }, /*#__PURE__*/React.createElement("section", {
      className: "bx-hero"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-hero-copy"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-eyebrow"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }), "Build your bundle"), /*#__PURE__*/React.createElement("h1", null, /*#__PURE__*/React.createElement("span", {
      className: "bh1-d"
    }, "Stack the Protocols.", /*#__PURE__*/React.createElement("br", null), "Stack the Savings."), /*#__PURE__*/React.createElement("span", {
      className: "bh1-m"
    }, "Stack the protocols.", /*#__PURE__*/React.createElement("br", null), "Stack the savings."))), /*#__PURE__*/React.createElement("div", {
      className: "bx-hero-badge",
      "aria-label": "Save up to 40% off retail today"
    }, /*#__PURE__*/React.createElement("span", {
      className: "up"
    }, "Save up to"), /*#__PURE__*/React.createElement("span", {
      className: "big"
    }, "40", /*#__PURE__*/React.createElement("span", {
      className: "pct"
    }, "%")), /*#__PURE__*/React.createElement("span", {
      className: "sub"
    }, "off retail today", /*#__PURE__*/React.createElement("span", {
      className: "bang"
    }, "!")))), /*#__PURE__*/React.createElement("div", {
      className: "bx-catalog-head"
    }, /*#__PURE__*/React.createElement("h3", null, "Add protocols")), /*#__PURE__*/React.createElement("div", {
      className: "bx-catalog"
    }, PRODUCTS.map(p => /*#__PURE__*/React.createElement(React.Fragment, {
      key: p.key
    }, /*#__PURE__*/React.createElement(ProductRow, {
      p: p,
      qty: cart[p.key] || 0,
      open: openKey === p.key,
      mode: mode,
      onAdd: addItem,
      onQty: setQty,
      onOpen: setOpenKey
    }), openKey === p.key && /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-inline"
    }, /*#__PURE__*/React.createElement(ProductDetail, {
      p: p,
      qty: cart[p.key] || 0,
      mode: mode,
      onAdd: addItem,
      onQty: setQty,
      cart: cart
    }))))), /*#__PURE__*/React.createElement("div", {
      className: "bx-detail-desktop"
    }, /*#__PURE__*/React.createElement(ProductDetail, {
      p: BY_KEY[openKey],
      qty: cart[openKey] || 0,
      mode: mode,
      onAdd: addItem,
      onQty: setQty
    }))), /*#__PURE__*/React.createElement(Summary, {
      cart: cart,
      mode: mode,
      setMode: setMode,
      cadence: cadence,
      setCadence: setCadence,
      onQty: setQty,
      onRemove: removeItem
    })), /*#__PURE__*/React.createElement(MobileCartSheet, {
      cart: cart,
      mode: mode,
      cadence: cadence
    }), /*#__PURE__*/React.createElement(PopularStacks, {
      mode: mode,
      onAddStack: addStack,
      cart: cart
    }), /*#__PURE__*/React.createElement("section", {
      className: "bx-calc-section bx-calc-section--coral bx-calc-desktop"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-section-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-eyebrow"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }), "Plan your supply"), /*#__PURE__*/React.createElement("h2", null, "Let’s build your barn’s plan."), /*#__PURE__*/React.createElement("p", null, "Answer 4 quick questions and we’ll tell you exactly what to put on auto-ship. No guesswork, no running short.")), /*#__PURE__*/React.createElement(BarnPlanner, {
      onApply: applyPlan
    })), /*#__PURE__*/React.createElement("div", {
      className: "bx-calc-band gx-barn-entry"
    }, /*#__PURE__*/React.createElement("div", {
      className: "gx-barn-entry-inner"
    }, /*#__PURE__*/React.createElement("button", {
      className: "gx-barn-entry-link",
      onClick: () => setCalcOpen(true)
    }, "Quick Calculator →"), /*#__PURE__*/React.createElement("span", {
      className: "gx-barn-entry-tx"
    }, /*#__PURE__*/React.createElement("b", null, "How much should I order?"), /*#__PURE__*/React.createElement("span", null, "Answer 4 questions & we’ll size it for you.")), /*#__PURE__*/React.createElement("span", {
      className: "gx-barn-entry-ic",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("svg", {
      width: "18",
      height: "18",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.9",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("rect", {
      x: "5",
      y: "3",
      width: "14",
      height: "18",
      rx: "2"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M9 7h6M9 11h6M9 15h3"
    }))))), /*#__PURE__*/React.createElement(CalcDrawer, {
      open: calcOpen,
      onClose: () => setCalcOpen(false),
      onApply: applyPlan
    }), /*#__PURE__*/React.createElement(Reviews, null), /*#__PURE__*/React.createElement(SubscriptionTrust, null));
  }
  function CalcDrawer({
    open,
    onClose,
    onApply
  }) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "gx-drawer-scrim",
      "data-open": open,
      onClick: onClose
    }), /*#__PURE__*/React.createElement("aside", {
      className: "gx-drawer gx-calc",
      "data-open": open,
      "aria-hidden": !open,
      role: "dialog",
      "aria-label": "Barn Plan Builder"
    }, /*#__PURE__*/React.createElement("div", {
      className: "gx-drawer-head"
    }, /*#__PURE__*/React.createElement("div", {
      className: "gx-calc-htext"
    }, /*#__PURE__*/React.createElement("span", {
      className: "gx-calc-eyebrow"
    }, "Barn Plan Builder"), /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, "How much should I order?")), /*#__PURE__*/React.createElement("button", {
      className: "gx-drawer-close",
      onClick: onClose,
      "aria-label": "Close"
    }, "×")), /*#__PURE__*/React.createElement("div", {
      className: "gx-calc-body"
    }, /*#__PURE__*/React.createElement("p", {
      className: "gx-calc-intro"
    }, "Answer 4 quick questions and we’ll size your supply for you. No guesswork, no running short."), open && /*#__PURE__*/React.createElement(BarnPlanner, {
      onApply: onApply
    }))));
  }

  /* ---------------- Popular stacks (guided "build for your goal") ---------------- */
  const STACKS = [{
    name: 'The Peterson Protocol',
    goal: 'The full foundation',
    items: {
      'gut-x': 1,
      'osteo-max': 1,
      'amino-max': 1
    },
    feature: true,
    blurb: 'Gut, joints, and topline together. The complete program behind the 90-day results.'
  }, {
    name: 'The Foundation Stack',
    goal: 'Mobility & digestion',
    items: {
      'gut-x': 1,
      'osteo-max': 1
    },
    blurb: 'The everyday pair. Settle the gut and support the joints in one shipment.'
  }, {
    name: 'Calm + Gut',
    goal: 'Show & travel focus',
    items: {
      'max-calm': 1,
      'gut-x': 1
    },
    blurb: 'Steady focus for show and sale horses, on a settled, well-absorbing gut.'
  }];
  function PopularStacks({
    mode,
    onAddStack,
    cart
  }) {
    const [added, setAdded] = useState(null);
    const [selected, setSelected] = useState((STACKS.find(s => s.feature) || STACKS[0]).name);
    const handleAdd = s => {
      setSelected(s.name);
      onAddStack(s.items);
      setAdded(s.name);
      setTimeout(() => setAdded(a => a === s.name ? null : a), 1600);
    };
    return /*#__PURE__*/React.createElement("section", {
      className: "bx-stacks"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-stacks-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "bx-eyebrow"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }), "Not sure where to start"), /*#__PURE__*/React.createElement("h2", null, "Popular stacks")), /*#__PURE__*/React.createElement("p", null, "Proven combinations our customers run together. Add one as your base, then adjust quantities in the builder.")), /*#__PURE__*/React.createElement("div", {
      className: "bx-stack-grid"
    }, STACKS.map(s => {
      const pr = priceCart(s.items, mode);
      const keys = Object.keys(s.items);
      return /*#__PURE__*/React.createElement("article", {
        className: 'bx-stack' + (s.feature ? ' feature' : '') + (selected === s.name ? ' is-selected' : ''),
        key: s.name,
        onClick: () => setSelected(s.name)
      }, s.feature && /*#__PURE__*/React.createElement("span", {
        className: "bx-stack-flag"
      }, "Most complete"), /*#__PURE__*/React.createElement("div", {
        className: "bx-stack-top"
      }, /*#__PURE__*/React.createElement("span", {
        className: "bx-stack-for"
      }, s.goal), /*#__PURE__*/React.createElement("h3", null, s.name), /*#__PURE__*/React.createElement("p", {
        className: "bx-stack-blurb"
      }, s.blurb)), /*#__PURE__*/React.createElement("div", {
        className: "bx-stack-items"
      }, keys.map(k => /*#__PURE__*/React.createElement("span", {
        className: "bx-stack-chip",
        key: k
      }, /*#__PURE__*/React.createElement("span", {
        className: "bx-stack-chip-dot",
        style: {
          background: BY_KEY[k].accent
        }
      }), BY_KEY[k].name))), /*#__PURE__*/React.createElement("div", {
        className: "bx-stack-foot"
      }, /*#__PURE__*/React.createElement("div", {
        className: "bx-stack-pricing"
      }, /*#__PURE__*/React.createElement("div", {
        className: "bx-stack-price-row"
      }, /*#__PURE__*/React.createElement("span", {
        className: "bx-stack-price"
      }, "$", pr.total.toFixed(2)), /*#__PURE__*/React.createElement("span", {
        className: "bx-stack-was"
      }, "$", pr.retail.toFixed(2))), /*#__PURE__*/React.createElement("span", {
        className: "bx-stack-meta"
      }, mode === 'sub' ? 'Subscribed · ' : 'One-time · ', "save $", pr.save.toFixed(2))), /*#__PURE__*/React.createElement("button", {
        className: 'bx-stack-add' + (added === s.name ? ' is-added' : ''),
        onClick: () => handleAdd(s)
      }, added === s.name ? 'Added \u2713' : 'Add stack')));
    })));
  }

  /* ---------------- Reviews (bundle / subscription flavored — PLACEHOLDER copy) ---------------- */
  const REVIEWS = [{
    name: 'Dana R.',
    loc: 'Ocala, FL',
    meta: 'Subscribed 14 months · 3 protocols',
    rating: 5,
    title: 'Three jugs on auto-ship and I never think about it',
    body: 'I run Gut X, Osteo-MAX, and Amino on the same subscription. One delivery, one charge, and my two performance horses have never looked better. The volume pricing is what got me to stack them.'
  }, {
    name: 'Mark T.',
    loc: 'Weatherford, TX',
    meta: 'Subscribed 8 months · 2 protocols',
    rating: 5,
    title: 'The gut + joint pair changed my older gelding',
    body: 'Started with just Gut X, added Osteo-MAX after a month. Manure firmed up and he moves out sound on cold mornings now. Bundling the two dropped the per-gallon cost enough that it was a no-brainer.'
  }, {
    name: 'Lacey M.',
    loc: 'Aiken, SC',
    meta: 'Subscribed 6 months · 2 protocols',
    rating: 5,
    title: 'Calm + Gut for my show mare',
    body: 'She travels every other weekend and used to go off her feed. Max Calm with Gut X keeps her eating and focused in the ring without any dullness. Editing my shipment before a big show week is easy.'
  }];
  function Stars({
    n,
    cls
  }) {
    return /*#__PURE__*/React.createElement("span", {
      className: cls,
      "aria-label": n + ' out of 5 stars'
    }, [0, 1, 2, 3, 4].map(i => /*#__PURE__*/React.createElement("svg", {
      key: i,
      width: "15",
      height: "15",
      viewBox: "0 0 24 24",
      fill: i < n ? 'currentColor' : 'none',
      stroke: "currentColor",
      strokeWidth: "1.6",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("polygon", {
      points: "12 2.5 15 9 22 9.7 16.8 14.4 18.3 21.5 12 17.8 5.7 21.5 7.2 14.4 2 9.7 9 9"
    }))));
  }
  function Reviews() {
    return /*#__PURE__*/React.createElement("section", {
      className: "bx-reviews"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-reviews-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "bx-eyebrow"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }), "From the herd"), /*#__PURE__*/React.createElement("h2", null, "Owners who stack, stay")), /*#__PURE__*/React.createElement("div", {
      className: "bx-rev-agg"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-rev-score"
    }, "4.9", /*#__PURE__*/React.createElement("small", null, "/5")), /*#__PURE__*/React.createElement("div", {
      className: "bx-rev-agg-txt"
    }, /*#__PURE__*/React.createElement(Stars, {
      n: 5,
      cls: "bx-rev-stars"
    }), /*#__PURE__*/React.createElement("span", null, "40,206 reviews across ", /*#__PURE__*/React.createElement("span", {
      className: "nw"
    }, "all protocols"))))), /*#__PURE__*/React.createElement("div", {
      className: "bx-rev-grid"
    }, REVIEWS.map(r => /*#__PURE__*/React.createElement("article", {
      className: "bx-rev",
      key: r.name
    }, /*#__PURE__*/React.createElement(Stars, {
      n: r.rating,
      cls: "bx-rev-stars-sm"
    }), /*#__PURE__*/React.createElement("h4", {
      className: "bx-rev-title"
    }, r.title), /*#__PURE__*/React.createElement("p", {
      className: "bx-rev-body"
    }, r.body), /*#__PURE__*/React.createElement("div", {
      className: "bx-rev-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-rev-av"
    }, r.name.split(' ').map(w => w[0]).join('')), /*#__PURE__*/React.createElement("div", {
      className: "bx-rev-who"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-rev-name"
    }, r.name, " ", /*#__PURE__*/React.createElement("span", {
      className: "bx-rev-verified"
    }, "Verified")), /*#__PURE__*/React.createElement("span", {
      className: "bx-rev-meta"
    }, r.meta)))))));
  }

  /* ---------------- Subscription reassurance strip ---------------- */
  const TRUST = [{
    ic: 'calendar',
    t: 'Skip, pause, or cancel anytime',
    d: 'No phone calls, manage it all from your account.'
  }, {
    ic: 'edit',
    t: 'Edit before every shipment',
    d: 'Swap protocols or change quantities whenever you like.'
  }, {
    ic: 'tag',
    t: 'No fees, no minimums',
    d: 'Just your subscriber price and the freedom to change anytime.'
  }, {
    ic: 'truck',
    t: 'Free shipping on subscriptions',
    d: 'Every recurring order ships free, right to the barn.'
  }];
  const TRUST_ICONS = {
    calendar: '<rect x="3" y="4.5" width="18" height="16" rx="2.5"/><path d="M3 9h18M8 2.5v4M16 2.5v4"/>',
    edit: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>',
    tag: '<path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2a2 2 0 0 1-.6-1.4V4a1 1 0 0 1 1-1h7.99a2 2 0 0 1 1.42.59l7.4 7.4a2 2 0 0 1 0 2.82z"/><circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" stroke="none"/>',
    truck: '<path d="M3 6.5h11v8H3z"/><path d="M14 9h4l3 3v2.5h-7z"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/>'
  };
  function SubscriptionTrust() {
    return /*#__PURE__*/React.createElement("section", {
      className: "bx-trust"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-trust-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-eyebrow"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }), "Subscribe with confidence"), /*#__PURE__*/React.createElement("h2", null, "Built around your barn, not a contract")), /*#__PURE__*/React.createElement("div", {
      className: "bx-trust-grid"
    }, TRUST.map(x => /*#__PURE__*/React.createElement("div", {
      className: "bx-trust-item",
      key: x.t
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-trust-ic",
      dangerouslySetInnerHTML: {
        __html: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' + TRUST_ICONS[x.ic] + '</svg>'
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "bx-trust-txt"
    }, /*#__PURE__*/React.createElement("strong", null, x.t), /*#__PURE__*/React.createElement("span", null, x.d))))));
  }

  /* ---------------- Ingredient wall (AG1-style "what's inside") ---------------- */
  const INGREDIENTS = [{
    cat: 'Beta Glucan',
    title: 'Immune priming',
    product: 'Gut X',
    tint: '#7a5b46',
    desc: 'Trains the immune system and steadies digestion.'
  }, {
    cat: 'Live Yeast',
    title: 'Hindgut health',
    product: 'Gut X',
    tint: '#9a7b3e',
    desc: 'Feeds the hindgut microbiome for cleaner fermentation.'
  }, {
    cat: 'Hyaluronic Acid',
    title: 'Joint cushion',
    product: 'Gut X · Osteo-MAX',
    tint: '#5e6b74',
    desc: 'Lubricates joints and supports the gut lining.'
  }, {
    cat: 'Glucosamine',
    title: 'Cartilage',
    product: 'Osteo-MAX',
    tint: '#6b6f5a',
    desc: 'A building block for cartilage and joint fluid.'
  }, {
    cat: 'Collagen',
    title: 'Bone & tendon',
    product: 'Osteo-MAX',
    tint: '#8a6650',
    desc: 'Supports bone density and soft-tissue repair.'
  }, {
    cat: 'MSM',
    title: 'Recovery',
    product: 'Osteo-MAX',
    tint: '#7d8088',
    desc: 'Organic sulfur for comfort after hard work.'
  }, {
    cat: 'Chondroitin',
    title: 'Mobility',
    product: 'Joint Flex Plus',
    tint: '#5a7068',
    desc: 'Helps cartilage hold water and resist wear.'
  }, {
    cat: 'Lysine',
    title: 'Topline',
    product: 'Amino-MAX',
    tint: '#9c8442',
    desc: 'Essential amino acid behind muscle and topline.'
  }, {
    cat: 'Magnesium',
    title: 'Calm & nerve',
    product: 'Max Calm · Hydra-MAX',
    tint: '#8d9aa3',
    desc: 'Steadies focus and supports nerve and muscle function.'
  }, {
    cat: 'L-Tryptophan',
    title: 'Steady focus',
    product: 'Max Calm',
    tint: '#7a8a5e',
    desc: 'A precursor that supports calm without dullness.'
  }, {
    cat: 'Electrolytes',
    title: 'Hydration',
    product: 'Hydra-MAX',
    tint: '#5b8198',
    desc: 'Sodium and potassium to replace what sweat takes.'
  }];
  function IngredientWall() {
    return /*#__PURE__*/React.createElement("section", {
      className: "bx-ingwall"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-ingwall-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "bx-eyebrow"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }), "What's inside"), /*#__PURE__*/React.createElement("h2", null, "30+ ingredients, formulated for results")), /*#__PURE__*/React.createElement("div", {
      className: "bx-ingwall-cta"
    }, /*#__PURE__*/React.createElement("a", {
      className: "bx-ingpill",
      href: "#ingredients"
    }, "View all ingredients"), /*#__PURE__*/React.createElement("a", {
      className: "bx-ingpill ghost",
      href: "#supplement-facts"
    }, "Supplement facts"))), /*#__PURE__*/React.createElement("div", {
      className: "bx-ingmarquee"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-ingtrack"
    }, [0, 1].map(dup => /*#__PURE__*/React.createElement("div", {
      className: "bx-inggroup",
      key: dup,
      "aria-hidden": dup === 1 ? 'true' : undefined
    }, INGREDIENTS.map(g => /*#__PURE__*/React.createElement("article", {
      className: "bx-ingcard",
      key: g.cat,
      style: {
        '--tint': g.tint
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-ingphoto"
    }, g.img ? /*#__PURE__*/React.createElement("img", {
      className: "bx-ingimg",
      src: g.img,
      alt: g.cat
    }) : /*#__PURE__*/React.createElement("span", {
      className: "bx-ingphoto-tag"
    }, "Photo")), /*#__PURE__*/React.createElement("div", {
      className: "bx-ingbody"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-ingcat"
    }, g.cat), /*#__PURE__*/React.createElement("h3", null, g.title), /*#__PURE__*/React.createElement("p", null, g.desc), /*#__PURE__*/React.createElement("span", {
      className: "bx-ingtag"
    }, g.product)))))))));
  }
  ReactDOM.createRoot(document.getElementById('bundles-root')).render(/*#__PURE__*/React.createElement(BundlesPage, null));
})();
